import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getToken } from "@/lib/api";
import { publicOrigin } from "@/lib/publicOrigin";
import { shareLinkCard } from "@/lib/nativeShare";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Radio, Users, X, Heart, Send, Copy, Loader2, Wifi, WifiOff, Video, MonitorUp,
  MonitorSmartphone, Mic, MicOff, VideoOff, ShieldCheck, CheckCircle2, CalendarClock, Image as ImageIcon, Clock, SwitchCamera, Sparkles, Target, Church, Bell, Gift, Smartphone, Download, BatteryLow, EyeOff, Wand2,
} from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { HlsPlayer } from "@/components/LiveRoom";
import { ObsIngestHealth, ObsUnstableBox } from "@/components/ObsIngestHealth";
import { LiveOfferingRecap } from "@/components/LiveOfferingRecap";
import { FileUpload, resolveMediaUrl, uploadMediaFile } from "@/components/FileUpload";
import { uploadVideoNativeBackground, isNativePlatform } from "@/lib/nativeVideoUpload";
import { createReplayFileWriter } from "@/lib/liveReplay";
import {
  rtmpBroadcastAvailable, ensureRtmpPermissions, startRtmpPreview, startRtmpBroadcast,
  stopRtmpBroadcast, stopRtmpPreview, switchRtmpCamera, setRtmpMuted, setRtmpFilter, addRtmpListener,
} from "@/lib/rtmpBroadcaster";
import { FX_PRESETS, DEFAULT_FX, fxFilterCss, fxMatrix, hasFx } from "@/lib/mediaFx";
import { clampBeauty, beautyCss, beautyBoost } from "@/lib/liveBeauty";
import { readLook, resolveLook, writeLook, hasChurchLook } from "@/lib/liveLook";
import { LiveFxStrip } from "@/components/live/LiveFxStrip";
import { LiveBeautySlider } from "@/components/live/LiveBeautySlider";
import { FloatingReactions, ReactionButtons, useFloatingReactions, emojisFromConfig } from "@/components/LiveReactions";
import { useApp } from "@/context/AppContext";
import { BlockUserDialog } from "@/components/BlockUserDialog";
import { useBlockedIds, useUserBlocked } from "@/lib/blocks";
import { offeringWording } from "@/lib/offeringLabel";
import { CURRENCIES, autoCurrency } from "@/lib/currencies";
import { getInstallPrompt, isIOS } from "@/lib/installPrompt";
import { maxDatetimeLocal } from "@/lib/scheduleLimits";
import { useScreenAwake } from "@/lib/screenAwake";
import { useLiveBatteryAlert } from "@/lib/battery";
import { openLiveSocket } from "@/lib/liveSocket";

const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");

// Lot 639 / 644 — filtre et lissage du direct mémorisés sur l'appareil (lib/liveLook : une mémoire par église).
// Lissage natif dans le flux RTMP : Android (RootEncoder BeautyFilterRender, lot 639) et iOS (CoreImage, lot 641).
const nativeBeautySupported = () => ["android", "ios"].includes(Capacitor.getPlatform());

// Desktop/web broadcasts only via OBS (RTMPS → recorded server-side by Cloudflare).
// Browser camera/screen (WebRTC/WHIP) was removed: unreliable replays + viewers stuck
// loading. Live from a phone happens in the native app (RTMPS). See renderMobile.
const SOURCES = [
  { key: "obs", label: "Logiciel / OBS", labelKey: "studio.src_obs", desc: "Qualité HD via OBS Studio (URL + clé RTMP)", descKey: "studio.src_obs_desc", icon: MonitorSmartphone },
];

// Wait for ICE gathering (trickle-less WHIP), max 2s.
const waitIce = (pc) => new Promise((res) => {
  if (pc.iceGatheringState === "complete") return res();
  const check = () => { if (pc.iceGatheringState === "complete") { pc.removeEventListener("icegatheringstatechange", check); res(); } };
  pc.addEventListener("icegatheringstatechange", check);
  setTimeout(res, 2000);
});

const SourceTile = ({ src, active, disabled, onClick }) => {
  const { t } = useApp();
  const Icon = src.icon;
  return (
    <button
      type="button" onClick={onClick} disabled={disabled}
      className="flex items-start gap-3 p-3.5 rounded-xl border-2 w-full text-left transition-colors duration-200 disabled:opacity-50"
      style={{
        borderColor: active ? "#2D5A4C" : "#283832",
        background: active ? "rgba(45,90,76,0.16)" : "#1E2925",
        boxShadow: active ? "0 0 15px rgba(45,90,76,0.25)" : "none",
      }}
      data-testid={`source-selector-${src.key}`}
    >
      <div className="h-10 w-10 rounded-lg grid place-items-center shrink-0" style={{ background: active ? "#2D5A4C" : "#131A18", color: active ? "#fff" : "#A0B0AA" }}>
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-white text-sm">{src.labelKey ? t(src.labelKey, src.label) : src.label}</div>
        <div className="text-xs mt-0.5" style={{ color: "#A0B0AA" }}>{src.descKey ? t(src.descKey, src.desc) : src.desc}</div>
      </div>
    </button>
  );
};

const CopyField = ({ label, value, testId }) => {
  const { t } = useApp();
  const copy = () => { navigator.clipboard.writeText(value || ""); toast.success(t("studio.copied", "{label} copié").replace("{label}", label)); };
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-xs font-semibold" style={{ color: "#A0B0AA" }}>{label}</div>
      <div className="flex items-center gap-2">
        <input readOnly value={value || ""} className="flex-1 min-w-0 h-9 rounded-lg px-3 text-xs font-mono truncate text-white" style={{ background: "#0A0D0C", border: "1px solid #283832" }} data-testid={testId} />
        <button onClick={copy} className="h-9 w-9 rounded-lg grid place-items-center shrink-0 text-gray-300 hover:text-white" style={{ background: "#283832" }} data-testid={`${testId}-copy`}><Copy size={14} /></button>
      </div>
    </div>
  );
};

const darkSelect = "w-full h-10 rounded-lg px-3 text-sm text-white outline-none";
const selectStyle = { background: "#0A0D0C", border: "1px solid #283832" };

// Native capture presets (portrait). RootEncoder/HaishinKit encode at these dimensions.
const RTMP_QUALITIES = {
  "720": { w: 1280, h: 720, br: 2500000, label: "720p" },
  "1080": { w: 1920, h: 1080, br: 4500000, label: "1080p" },
};

export const LiveStudio = ({ user, onClose, initialChurchId = "", initialTitle = "", resumeLiveId = "", scheduledId = "" }) => {
  const { config, t } = useApp();
  const navigate = useNavigate();
  const reactionEmojis = emojisFromConfig(config);
  const [phase, setPhase] = useState("setup"); // setup | live
  // Only the native mobile app broadcasts from the camera (RTMPS). Everywhere on the
  // web the source is OBS (desktop) — mobile web shows an "install the app" screen.
  const [source, setSource] = useState(() => {
    if (resumeLiveId || scheduledId) return "obs";
    const phone = typeof window !== "undefined" && window.innerWidth < 1024;
    return (isNativePlatform() && phone) ? "webcam" : "obs";
  });
  const [form, setForm] = useState({ title: initialTitle, description: "", accept_offerings: true, offering_goal: "" });
  const [myChurches, setMyChurches] = useState([]);
  const [churchId, setChurchId] = useState("");
  const [cams, setCams] = useState([]);
  const [mics, setMics] = useState([]);
  const [camId, setCamId] = useState("");
  const [micId, setMicId] = useState("");
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [camError, setCamError] = useState("");
  const [hasStream, setHasStream] = useState(false);
  const [live, setLive] = useState(null);
  const [keyErr, setKeyErr] = useState("");
  const [pb, setPb] = useState({ status: "waiting", is_live: false, hls_url: null });
  // Aperçu qui charge par moments (réception sur CET appareil) — information neutre, pas un diagnostic OBS.
  const [previewBuffering, setPreviewBuffering] = useState(false);
  // Santé RÉELLE de la liaison OBS → Cloudflare (GET /live/{id}/ingest : état de l'entrée + coupures sur 5 min).
  const [ingest, setIngest] = useState(null);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [ended, setEnded] = useState(false); // live finished → show a clean "terminé" screen (not the camera loader)
  const [endConfirm, setEndConfirm] = useState(false); // styled in-app "end live?" dialog (no native window.confirm)
  const [scheduleAt, setScheduleAt] = useState("");
  const [reminderLead, setReminderLead] = useState(10); // host reminder lead: 5 / 10 / 30 min
  const [scheduling, setScheduling] = useState(false);
  const [thumbUrl, setThumbUrl] = useState("");
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 1024);
  const [facing, setFacing] = useState("user");
  const [beauty, setBeauty] = useState(() => readLook("").beauty); // lot 642 — intensité 0..100 du lissage (mémorisée sur l'appareil)
  const [beautyOpen, setBeautyOpen] = useState(false);
  const [comparing, setComparing] = useState(false); // lot 643 — doigt maintenu sur l'aperçu → image sans lissage
  const compareTimerRef = useRef(null);
  const [liveFx, setLiveFx] = useState(() => readLook("").fx); // lot 639 — preset couleur du direct (caméra intégrée)
  const [fxOpen, setFxOpen] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [replayUpload, setReplayUpload] = useState({ active: false, progress: 0, phase: null });
  // Ensure the studio's onClose fires at most once (the background replay upload and
  // the pill's "Fermer" button can both try to close), and skip state updates after
  // unmount while the detached finalizeReplay promise is still running.
  const mountedRef = useRef(true);
  const closedRef = useRef(false);
  const closeOnce = useCallback(() => { if (closedRef.current) return; closedRef.current = true; onClose?.(); }, [onClose]);
  const setRU = useCallback((v) => { if (mountedRef.current) setReplayUpload(v); }, []);
  useEffect(() => () => { mountedRef.current = false; }, []);
  const [recording, setRecording] = useState(false);
  // Native mobile RTMPS broadcast (RootEncoder/HaishinKit) — camera streams straight
  // to Cloudflare (server-recorded replay, no heavy upload). See lib/rtmpBroadcaster.
  const [nativeRtmpReady, setNativeRtmpReady] = useState(false);
  const [nativeRtmpErr, setNativeRtmpErr] = useState("");
  const nativeRtmpRef = useRef(false);
  // Native broadcast health (for the quality badge + auto-reconnect UX).
  const [nativeStreamStatus, setNativeStreamStatus] = useState("idle"); // idle | connecting | live | reconnecting
  const [nativeBitrate, setNativeBitrate] = useState(0); // kbps, from the plugin's newBitrate events
  // "Réseau faible" banner: ON after 5 consecutive seconds under 55% of the target
  // bitrate, OFF after 6 consecutive seconds above 75% (hysteresis — no flicker).
  const [lowNetwork, setLowNetwork] = useState(false);
  const lowTicksRef = useRef(0);
  const okTicksRef = useRef(0);
  const nativeStatusRef = useRef("idle");
  const autoStoppedRef = useRef(false);
  // Lot 600 — reprise automatique : début de la coupure (bandeau avec chrono) + relances JS de secours.
  const reconnectSinceRef = useRef(0);
  const relaunchCountRef = useRef(0);
  // Native capture resolution the host can pick (RootEncoder/HaishinKit encode at these).
  const [rtmpQuality, setRtmpQuality] = useState("720");
  const rtmpQualityRef = useRef("720");

  const videoRef = useRef(null);
  const obsHlsRef = useRef(null);
  const netStallsRef = useRef([]);
  const streamRef = useRef(null);
  const rawStreamRef = useRef(null);
  const beautyCleanupRef = useRef(null);
  const cdRef = useRef(null);
  const pcRef = useRef(null);
  const whipResourceRef = useRef(null);
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recAttemptedRef = useRef(false);
  const recStartRef = useRef(0);
  const recMimeRef = useRef("");
  const recDimsRef = useRef(null);
  const recCanvasCleanupRef = useRef(null);
  const diskWriterRef = useRef(null);
  const wsRef = useRef(null);
  const [recapOpen, setRecapOpen] = useState(false);
  const commentsEndRef = useRef(null);
  const liveRef = useRef(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [nowTs, setNowTs] = useState(Date.now());
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const blockedIds = useBlockedIds();
  const [blockTarget, setBlockTarget] = useState(null);
  useUserBlocked((id) => {
    if (!id) return;
    setComments((c) => c.filter((x) => x.userId !== id));
    wsRef.current?.send({ type: "block", user_id: id });
  });
  const [floats, pushFloat] = useFloatingReactions();
  const [elig, setElig] = useState(null);

  // Pre-flight live eligibility ("+X abonnés") updated in REAL TIME over WebSocket:
  // a new follower instantly refreshes the gate. A slow 60s poll + refetch-on-focus
  // remain as a safety net in case the socket drops.
  useEffect(() => {
    if (phase !== "setup" || !user) return undefined;
    let alive = true;
    let debounce = null;
    const load = async () => {
      try { const { data } = await api.get("/live/eligibility"); if (alive) setElig(data); } catch { /* noop */ }
    };
    const scheduleLoad = () => { if (debounce) clearTimeout(debounce); debounce = setTimeout(load, 400); };
    load();
    const iv = setInterval(load, 60000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    let closed = false, pingId = null, reconnect = null, ws = null;
    const connect = () => {
      if (closed) return;
      try { ws = new WebSocket(`${WS_BASE}/api/ws/notifications?token=${getToken()}`); } catch { return; }
      ws.onopen = () => { pingId = setInterval(() => { try { ws.send("ping"); } catch { /* noop */ } }, 25000); };
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "notification" && msg.notification && msg.notification.type === "follow") scheduleLoad();
        } catch { /* ignore */ }
      };
      ws.onclose = () => { if (pingId) clearInterval(pingId); if (!closed) reconnect = setTimeout(connect, 4000); };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    return () => {
      alive = false; closed = true;
      clearInterval(iv);
      if (debounce) clearTimeout(debounce);
      if (pingId) clearInterval(pingId);
      if (reconnect) clearTimeout(reconnect);
      window.removeEventListener("focus", onFocus);
      try { ws && ws.close(); } catch { /* noop */ }
    };
  }, [phase, user]);

  // Whether this host can toggle offerings. STRICT rule (matches the backend +
  // the donation checkout): only Preacher / Artist / Influencer whose privileges
  // allow receiving offerings — NOT super_admin — so the viewer donate button is
  // never shown for a host who cannot actually receive.
  const canOffer = ["preacher", "artist", "influencer"].includes(user?.role) && !!elig?.can_receive_offerings;
  const offer = offeringWording(user?.role, t);
  const showGoal = (config?.settings?.features?.offering_goal) !== false;
  const curSym = (CURRENCIES.find((c) => c.code === autoCurrency({ userCountry: user?.country, preferredCurrency: user?.preferred_currency, defaultCurrency: config?.settings?.default_currency })) || {}).symbol || "";
  const offeringGoal = Number(pb.offering_goal || form.offering_goal || 0);
  const offeringPct = offeringGoal > 0 ? Math.min(100, Math.round((Number(pb.offering_total || 0) / offeringGoal) * 100)) : 0;
  const isBrowserSource = source === "webcam" || source === "screen";
  // Native mobile RTMPS is the ONLY camera-broadcast path: running natively, on a phone
  // layout, camera source, with the native plugin present. It streams straight to
  // Cloudflare (server-recorded replay — no fragile browser recording/upload).
  const nativeRtmp = isNativePlatform() && isMobile && source === "webcam"
    && rtmpBroadcastAvailable();

  const minDatetimeLocal = () => {
    const d = new Date(Date.now() + 5 * 60000);
    const l = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return l.toISOString().slice(0, 16);
  };
  const scheduleBroadcast = async () => {
    if (!form.title.trim()) return toast.error(t("studio.schedule_need_title", "Ajoute un titre pour programmer le direct."));
    if (!scheduleAt) return toast.error(t("studio.schedule_need_date", "Choisis une date et une heure."));
    setScheduling(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description,
        accept_offerings: form.accept_offerings && canOffer,
        thumbnail_url: thumbUrl || undefined,
        church_id: churchId || undefined,
        as_self: !churchId,
        scheduled_at: new Date(scheduleAt).toISOString(),
        reminder_lead_min: reminderLead,
      };
      if (scheduledId) {
        // Editing an existing scheduled live → update IN PLACE (never create a duplicate).
        await api.patch(`/live/scheduled/${scheduledId}`, payload);
        toast.success(t("studio.schedule_updated", "Direct programmé mis à jour ✓"));
      } else {
        await api.post("/live/schedule", payload);
        toast.success(t("studio.schedule_done", "Direct programmé ✓ — tes abonnés seront prévenus 🔔"));
      }
      closeOnce();
    } catch (e) { toast.error(e?.friendlyMessage || t("studio.error", "Erreur")); }
    finally { setScheduling(false); }
  };

  const attach = (stream) => {
    streamRef.current = stream;
    setHasStream(true);
    if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play?.().catch(() => {}); }
  };

  // Light "beauty" filter (mobile): draw the raw camera onto a canvas with a soft
  // filter and publish the canvas stream, so viewers see it too. Off by default.
  // Lot 639 — the same canvas also applies the colour preset (lib/mediaFx) on the web path.
  // Lot 642 — le CSS courant vit dans une ref : le curseur met à jour le filtre sans reconstruire le flux.
  const fxCssRef = useRef("none");
  const composeFxCss = (level, presetKey) => [beautyCss(level), fxFilterCss({ ...DEFAULT_FX, preset: presetKey })].filter((f) => f && f !== "none").join(" ") || "none";
  const needsFxCanvas = (level, presetKey) => level > 0 || (presetKey && presetKey !== "normal");
  const stopBeauty = () => {
    const b = beautyCleanupRef.current;
    if (b) {
      cancelAnimationFrame(b.raf);
      try { b.video.pause(); b.video.srcObject = null; } catch { /* noop */ }
      beautyCleanupRef.current = null;
    }
  };
  const buildFxStream = (rawStream, level, presetKey = "normal") => {
    const v = document.createElement("video");
    v.muted = true; v.playsInline = true; v.autoplay = true; v.srcObject = rawStream;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    fxCssRef.current = composeFxCss(level, presetKey);
    beautyCleanupRef.current = { video: v, canvas, raf: 0 };
    const draw = () => {
      if (v.videoWidth) {
        if (canvas.width !== v.videoWidth) { canvas.width = v.videoWidth; canvas.height = v.videoHeight; }
        ctx.filter = fxCssRef.current;
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      }
      if (beautyCleanupRef.current) beautyCleanupRef.current.raf = requestAnimationFrame(draw);
    };
    const out = canvas.captureStream(30);
    rawStream.getAudioTracks().forEach((t) => out.addTrack(t));
    v.play?.().catch(() => {});
    draw();
    return out;
  };

  const stopLocal = useCallback(() => {
    stopBeauty();
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    try { rawStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    streamRef.current = null;
    rawStreamRef.current = null;
    setHasStream(false);
  }, []);

  // Re-attach the MediaStream whenever the <video> (re)mounts — the element is
  // rendered conditionally / across setup↔live phases, so srcObject set inside
  // attach() can run before the element exists. This effect fixes the black preview.
  useEffect(() => {
    const v = videoRef.current;
    if (v && streamRef.current && v.srcObject !== streamRef.current) {
      v.srcObject = streamRef.current;
      v.play?.().catch(() => {});
    }
  }, [hasStream, phase, source, isMobile]);

  // Acquire webcam
  const initWebcam = useCallback(async (nextCam, nextMic) => {
    setCamError("");
    try {
      stopLocal();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: nextCam ? { deviceId: { exact: nextCam } } : true,
        audio: nextMic ? { deviceId: { exact: nextMic } } : true,
      });
      attach(stream);
      stream.getVideoTracks().forEach((t) => (t.enabled = camOn));
      stream.getAudioTracks().forEach((t) => (t.enabled = micOn));
      const devices = await navigator.mediaDevices.enumerateDevices();
      const vid = devices.filter((d) => d.kind === "videoinput");
      const aud = devices.filter((d) => d.kind === "audioinput");
      setCams(vid); setMics(aud);
      if (!nextCam && vid[0]) setCamId(vid[0].deviceId);
      if (!nextMic && aud[0]) setMicId(aud[0].deviceId);
    } catch (e) {
      setCamError(e?.name === "NotAllowedError" ? t("studio.cam_denied", "Accès caméra refusé. Autorise la caméra dans ton navigateur.") : (e?.name === "NotFoundError" ? t("studio.cam_none", "Aucune caméra détectée.") : t("studio.cam_fail", "Impossible d'accéder à la caméra.")));
    }
  }, [camOn, micOn, stopLocal]);

  // Acquire screen share (+ mic for narration)
  const initScreen = useCallback(async () => {
    setCamError("");
    try {
      stopLocal();
      const disp = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      let mic = null;
      try { mic = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch { /* mic optional */ }
      const stream = new MediaStream();
      disp.getVideoTracks().forEach((t) => stream.addTrack(t));
      const micTrack = mic?.getAudioTracks?.()[0];
      const dispAudio = disp.getAudioTracks?.()[0];
      if (micTrack) stream.addTrack(micTrack); else if (dispAudio) stream.addTrack(dispAudio);
      attach(stream);
      // If the user hits the browser "Stop sharing" button
      disp.getVideoTracks()[0].addEventListener("ended", () => {
        if (liveRef.current && phase === "live") endLive();
        else { setSource("webcam"); }
      });
    } catch (e) {
      setCamError("Partage d'écran annulé ou refusé.");
      setSource("webcam");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopLocal, phase]);

  // Mobile (TikTok/Facebook-style): full-screen camera, no config — uses facingMode instead of device pickers.
  useEffect(() => {
    const on = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const initMobileCam = useCallback(async (facingMode) => {
    setCamError("");
    try {
      stopLocal();
      const raw = await navigator.mediaDevices.getUserMedia({ video: { facingMode }, audio: true });
      rawStreamRef.current = raw;
      raw.getVideoTracks().forEach((t) => (t.enabled = camOn));
      raw.getAudioTracks().forEach((t) => (t.enabled = micOn));
      attach(needsFxCanvas(beauty, liveFx) ? buildFxStream(raw, beauty, liveFx) : raw);
    } catch (e) {
      setCamError(e?.name === "NotAllowedError" ? t("studio.cam_denied", "Accès caméra refusé. Autorise la caméra dans ton navigateur.") : (e?.name === "NotFoundError" ? t("studio.cam_none", "Aucune caméra détectée.") : t("studio.cam_fail", "Impossible d'accéder à la caméra.")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camOn, micOn, stopLocal, beauty, liveFx]);
  const flipCamera = () => {
    const f = facing === "user" ? "environment" : "user"; setFacing(f);
    if (nativeRtmpRef.current) switchRtmpCamera(); else initMobileCam(f);
  };

  // Native RTMPS camera preview (renders behind the transparent WebView).
  const startNativePreview = useCallback(async () => {
    setNativeRtmpErr("");
    try {
      const ok = await ensureRtmpPermissions();
      if (!ok) { setNativeRtmpErr("Autorise la caméra et le micro pour passer en direct."); return; }
      document.documentElement.classList.add("yalola-rtmp-live");
      const q = RTMP_QUALITIES[rtmpQualityRef.current] || RTMP_QUALITIES["720"];
      await startRtmpPreview({ facing, width: q.w, height: q.h, bitrate: q.br });
      nativeRtmpRef.current = true;
      setNativeRtmpReady(true);
      setRtmpMuted(!micOn);
    } catch {
      setNativeRtmpErr("Impossible d'ouvrir la caméra.");
      document.documentElement.classList.remove("yalola-rtmp-live");
    }
  }, [facing, micOn]);

  const stopNativePreview = useCallback(async () => {
    nativeRtmpRef.current = false;
    setNativeRtmpReady(false);
    document.documentElement.classList.remove("yalola-rtmp-live");
    await stopRtmpPreview();
  }, []);

  // Change capture resolution (restarts the native preview to re-prepare the encoder).
  const changeRtmpQuality = async (q) => {
    if (q === rtmpQualityRef.current) return;
    rtmpQualityRef.current = q;
    setRtmpQuality(q);
    if (nativeRtmpRef.current) { await stopNativePreview(); await startNativePreview(); }
  };

  // Web path: rebuild the canvas pipeline with the current Beauty level + colour preset (native: see the effect below).
  const rebuildWebFx = (level, presetKey) => {
    if (!rawStreamRef.current || nativeRtmpRef.current) return;
    const old = streamRef.current;
    stopBeauty();
    const out = needsFxCanvas(level, presetKey) ? buildFxStream(rawStreamRef.current, level, presetKey) : rawStreamRef.current;
    if (old && old !== rawStreamRef.current) { try { old.getVideoTracks().forEach((t) => t.stop()); } catch { /* noop */ } }
    attach(out);
  };
  // Lot 642 — curseur d'intensité : le canvas web n'est reconstruit qu'au passage 0 ↔ actif, sinon simple mise à jour du filtre.
  const setBeautyLevel = (raw) => {
    const next = clampBeauty(raw);
    const rebuild = needsFxCanvas(beauty, liveFx) !== needsFxCanvas(next, liveFx);
    setBeauty(next);
    writeLook(churchId, { fx: liveFx, beauty: next });
    shareLook(churchId, liveFx, next);
    if (rebuild) rebuildWebFx(next, liveFx);
    else fxCssRef.current = composeFxCss(next, liveFx);
  };
  // Lot 639 — preset couleur du direct (même bandeau que la caméra intégrée), mémorisé sur l'appareil (lot 644 : par église).
  const pickLiveFx = (key) => {
    setLiveFx(key);
    writeLook(churchId, { fx: key, beauty });
    shareLook(churchId, key, beauty);
    rebuildWebFx(beauty, key);
  };
  // Lot 644 — changement d'identité de diffusion : on rappelle le look mémorisé de l'église (ou le look personnel).
  // Lot 645 — le look PARTAGÉ de l'église (live_look, posé par n'importe quel gestionnaire) prime sur la mémoire locale.
  const lookScopeRef = useRef("");
  const sharedLookOf = (cid) => (cid ? myChurches.find((c) => c.id === cid)?.live_look : null) || null;
  useEffect(() => {
    if (lookScopeRef.current === churchId) return;
    if (churchId && myChurches.length === 0) return; // la liste des églises (et leur look partagé) n'est pas encore là
    lookScopeRef.current = churchId;
    const shared = sharedLookOf(churchId);
    const look = shared ? { fx: FX_PRESETS.some((p) => p.key === shared.fx) ? shared.fx : "normal", beauty: clampBeauty(shared.beauty) } : resolveLook(churchId);
    if (shared) writeLook(churchId, look);
    if (look.fx === liveFx && look.beauty === beauty) return;
    setLiveFx(look.fx);
    setBeauty(look.beauty);
    rebuildWebFx(look.beauty, look.fx);
  }, [churchId, myChurches]); // eslint-disable-line react-hooks/exhaustive-deps
  // Lot 645 — publication différée du look vers l'église (un seul appel après le dernier geste), puis état local à jour.
  const shareLookTimerRef = useRef(null);
  const shareLook = (cid, fx, level) => {
    if (!cid) return;
    clearTimeout(shareLookTimerRef.current);
    shareLookTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.put(`/churches/${cid}/live-look`, { fx, beauty: level });
        setMyChurches((list) => list.map((c) => (c.id === cid ? { ...c, live_look: data.live_look } : c)));
      } catch { /* hors ligne ou droits retirés : la mémoire locale reste */ }
    }, 800);
  };
  useEffect(() => () => clearTimeout(shareLookTimerRef.current), []);
  // Native RTMPS: push the colour matrix (+ smoothing intensity) into the GL/CoreImage pipeline → preview, stream and replay.
  // Lot 643 — pendant la comparaison (doigt maintenu), le lissage et son gain couleur sont retirés, le preset reste.
  const effectiveBeauty = comparing ? 0 : beauty;
  useEffect(() => {
    if (!nativeRtmp || !nativeRtmpReady) return;
    const fx = { ...DEFAULT_FX, preset: liveFx, ...beautyBoost(effectiveBeauty) };
    const identity = !hasFx(fx);
    // Lot 642 — léger différé pour ne pas inonder le pont natif pendant le glissement du curseur.
    const id = setTimeout(() => setRtmpFilter({ matrix: identity ? null : fxMatrix(fx), identity, beauty: nativeBeautySupported() ? effectiveBeauty : 0 }), 60);
    return () => clearTimeout(id);
  }, [nativeRtmp, nativeRtmpReady, liveFx, effectiveBeauty]);
  // Web (canvas) : même bascule, sans reconstruire le flux.
  useEffect(() => {
    if (nativeRtmpRef.current || !beautyCleanupRef.current) return;
    fxCssRef.current = composeFxCss(effectiveBeauty, liveFx);
  }, [effectiveBeauty, liveFx]); // eslint-disable-line react-hooks/exhaustive-deps
  const startCompare = (e) => {
    if (beauty <= 0) return;
    e.preventDefault?.();
    clearTimeout(compareTimerRef.current);
    compareTimerRef.current = setTimeout(() => setComparing(true), 120);
  };
  const stopCompare = () => {
    clearTimeout(compareTimerRef.current);
    setComparing(false);
  };
  useEffect(() => () => clearTimeout(compareTimerRef.current), []);
  const fxAvailable = source === "webcam" && (nativeRtmp || phase === "setup");
  const beautyAvailable = fxAvailable && (!nativeRtmp || nativeBeautySupported());
  const compareAvailable = beautyAvailable && beauty > 0 && !ended && countdown === null && (!nativeRtmp || nativeRtmpReady);
  const fxThumb = user?.avatar_url ? resolveMediaUrl(user.avatar_url) : null;
  const startCountdown = () => {
    if (countdown !== null || !canGoLive || starting || !gateOk) return;
    setCountdown(3);
    cdRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(cdRef.current); cdRef.current = null; setTimeout(() => goLive(), 0); return null; }
        return c - 1;
      });
    }, 1000);
  };
  const cancelCountdown = () => { if (cdRef.current) { clearInterval(cdRef.current); cdRef.current = null; } setCountdown(null); };

  // React to source change (browser sources need a preview)
  useEffect(() => {
    if (source === "webcam") {
      if (nativeRtmp) { stopLocal(); startNativePreview(); }
      else { if (nativeRtmpRef.current) stopNativePreview(); if (isMobile) initMobileCam(facing); else initWebcam(camId || undefined, micId || undefined); }
    }
    else if (source === "screen") { if (nativeRtmpRef.current) stopNativePreview(); initScreen(); }
    else { if (nativeRtmpRef.current) stopNativePreview(); stopLocal(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, isMobile, nativeRtmp]);

  // Release the native broadcaster on unmount (foreground camera must be freed).
  useEffect(() => () => {
    if (nativeRtmpRef.current) {
      try { stopRtmpBroadcast(); } catch { /* noop */ }
      try { stopRtmpPreview(); } catch { /* noop */ }
      document.documentElement.classList.remove("yalola-rtmp-live");
    }
  }, []);

  // Bridge native broadcast events → quality badge + reconnect UX.
  useEffect(() => {
    if (!nativeRtmp) return undefined;
    const subs = [];
    let relaunchTimer = null;
    const add = (evt, cb) => addRtmpListener(evt, cb).then((s) => subs.push(s)).catch(() => {});
    const setStatus = (s) => { nativeStatusRef.current = s; setNativeStreamStatus(s); };
    const resetLowNet = () => { lowTicksRef.current = 0; okTicksRef.current = 0; setLowNetwork(false); };
    // Lot 600 — coupure réseau : le plugin retente en boucle (plus de budget de 8 essais) ; le bandeau
    // « Reconnexion… » affiche le temps écoulé et l'hôte n'a rien à relancer. Si une ancienne version du
    // plugin abandonne quand même (retry=false), on relance nous-mêmes la diffusion toutes les 5 s.
    const markReconnecting = () => { if (!reconnectSinceRef.current) reconnectSinceRef.current = Date.now(); setStatus("reconnecting"); };
    const relaunch = () => {
      clearTimeout(relaunchTimer);
      relaunchTimer = setTimeout(async () => {
        const l = liveRef.current;
        if (!l?.rtmps_url || !nativeRtmpRef.current || nativeStatusRef.current !== "reconnecting") return;
        if (++relaunchCountRef.current > 60) { setStatus("idle"); toast.error(t("live.connection_lost", "Connexion au serveur perdue.")); return; }
        try { await stopRtmpBroadcast(); await startRtmpBroadcast({ url: l.rtmps_url, key: l.rtmps_key || "" }); }
        catch { relaunch(); }
      }, 5000);
    };
    add("connectionStarted", () => { if (nativeStatusRef.current !== "reconnecting") setStatus("connecting"); resetLowNet(); });
    add("connectionSuccess", () => {
      clearTimeout(relaunchTimer);
      relaunchCountRef.current = 0;
      if (reconnectSinceRef.current) { reconnectSinceRef.current = 0; toast.success(t("live.resumed", "Connexion rétablie — tu es de nouveau en direct ✅")); }
      setStatus("live");
    });
    add("connectionFailed", (e) => {
      resetLowNet();
      if (!liveRef.current || !nativeRtmpRef.current) { setStatus("idle"); return; }
      markReconnecting();
      if (!(e && e.retry)) relaunch();
    });
    add("disconnect", () => {
      resetLowNet();
      if (nativeStatusRef.current === "live") markReconnecting();
    });
    add("authError", () => toast.error(t("live.key_rejected", "La clé de diffusion a été refusée par le serveur.")));
    add("newBitrate", (e) => {
      const kbps = Math.round((Number(e?.bitrate) || 0) / 1000);
      setNativeBitrate(kbps);
      if (nativeStatusRef.current !== "live" || kbps <= 0) return;
      const target = (RTMP_QUALITIES[rtmpQualityRef.current] || RTMP_QUALITIES["720"]).br / 1000;
      if (kbps < target * 0.55) {
        okTicksRef.current = 0;
        if (++lowTicksRef.current >= 5) setLowNetwork(true);
      } else if (kbps > target * 0.75) {
        lowTicksRef.current = 0;
        if (++okTicksRef.current >= 6) setLowNetwork(false);
      } else {
        lowTicksRef.current = 0; okTicksRef.current = 0; // neutral zone: hold current state
      }
    });
    return () => { clearTimeout(relaunchTimer); subs.forEach((s) => { try { s.remove(); } catch { /* noop */ } }); };
  }, [nativeRtmp]);

  useEffect(() => {
    api.get("/churches/mine").then(({ data }) => {
      const chs = data.churches || [];
      setMyChurches(chs);
      if (initialChurchId && chs.some((c) => c.id === initialChurchId)) { setChurchId(initialChurchId); return; }
      // Pre-select the church the host is responsible for: his lives land on the church
      // page by default ("Moi" stays an explicit choice → sent as as_self).
      const owned = chs.filter((c) => c.owned);
      if (!resumeLiveId && !scheduledId && owned.length === 1) setChurchId(owned[0].id);
    }).catch(() => {});
  }, [initialChurchId, resumeLiveId, scheduledId]);
  useEffect(() => () => { stopLocal(); try { pcRef.current?.close(); } catch { /* noop */ } try { recorderRef.current?.stop(); } catch { /* noop */ } try { recCanvasCleanupRef.current?.(); } catch { /* noop */ } if (cdRef.current) clearInterval(cdRef.current); }, [stopLocal]);


  const changeCam = (id) => { setCamId(id); if (source === "webcam") initWebcam(id, micId || undefined); };
  const changeMic = (id) => { setMicId(id); if (source === "webcam") initWebcam(camId || undefined, id); };
  const toggleCam = () => { const v = !camOn; setCamOn(v); streamRef.current?.getVideoTracks().forEach((t) => (t.enabled = v)); };
  const toggleMic = () => { const v = !micOn; setMicOn(v); if (nativeRtmpRef.current) setRtmpMuted(!v); else streamRef.current?.getAudioTracks().forEach((t) => (t.enabled = v)); };

  // Create the Cloudflare live input once (idempotent). Title is optional here —
  // YouTube/Facebook style: get the ingest key immediately, set the title anytime.
  const ensureLive = useCallback(async () => {
    if (liveRef.current) return liveRef.current;
    const title = form.title.trim();
    const { data } = await api.post("/live/start", { ...form, title, accept_offerings: form.accept_offerings && canOffer, offering_goal: (form.accept_offerings && canOffer && Number(form.offering_goal) > 0) ? Number(form.offering_goal) : undefined, thumbnail_url: thumbUrl || undefined, church_id: churchId || undefined, as_self: !churchId, source: isMobile ? "mobile" : "pc" });
    liveRef.current = data.live;
    setLive(data.live);
    return data.live;
  }, [form, canOffer, user, thumbUrl, churchId, isMobile]);

  // Auto-generate the ingest key as soon as OBS is selected (no title required).
  // One silent auto-retry, then surface a "Réessayer" button instead of an endless spinner.
  const genKey = useCallback(async (attempt = 0) => {
    setKeyErr("");
    try {
      await ensureLive();
    } catch (e) {
      if (attempt < 1) {
        setTimeout(() => { genKey(attempt + 1); }, 1500);
        return;
      }
      setKeyErr(e?.friendlyMessage || t("studio.key_fail", "Impossible de générer la clé de stream. Vérifie ta connexion et réessaie."));
    }
  }, [ensureLive]);

  useEffect(() => {
    if (source === "obs" && !live && !resumeLiveId && !scheduledId) genKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  // Resume an already-created live in the FULL studio (not the read-only host
  // monitor): load its data, bind OBS source, and let the playback poll flip
  // into the live phase automatically. No new Cloudflare input is created.
  useEffect(() => {
    if (!resumeLiveId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/live/${resumeLiveId}`);
        if (cancelled || !data?.live) return;
        const l = data.live;
        liveRef.current = l;
        setLive(l);
        setSource("obs");
        setForm((f) => ({
          ...f,
          title: l.title || f.title,
          description: l.description || "",
          accept_offerings: !!l.accept_offerings,
          offering_goal: l.offering_goal ? String(l.offering_goal) : "",
        }));
        if (l.church_id) setChurchId(l.church_id);
        if (l.thumbnail_url) setThumbUrl(l.thumbnail_url);
      } catch {
        toast.error(t("studio.reopen_fail", "Impossible de rouvrir ce direct."));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeLiveId]);

  // Open the studio from a SCHEDULED live: restore title, description, date/time,
  // church, thumbnail AND the persistent OBS keys (server attaches a stable
  // Cloudflare input to the schedule, so keys don't change on each re-open).
  useEffect(() => {
    if (!scheduledId) return undefined;
    let cancelled = false;
    const toLocalInput = (isoStr) => {
      try { const d = new Date(isoStr); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); } catch { return ""; }
    };
    (async () => {
      try {
        const { data } = await api.post(`/live/scheduled/${scheduledId}/studio`);
        if (cancelled) return;
        const l = data.live;
        const s = data.scheduled || {};
        if (l) { liveRef.current = l; setLive(l); }
        setSource("obs");
        setForm((f) => ({
          ...f,
          title: s.title || l?.title || f.title,
          description: s.description || l?.description || "",
          accept_offerings: !!(l?.accept_offerings ?? s.accept_offerings),
          offering_goal: l?.offering_goal ? String(l.offering_goal) : "",
        }));
        if (s.church_id || l?.church_id) setChurchId(s.church_id || l.church_id);
        if (s.thumbnail_url || l?.thumbnail_url) setThumbUrl(s.thumbnail_url || l.thumbnail_url);
        if (s.scheduled_at) setScheduleAt(toLocalInput(s.scheduled_at));
        if (s.reminder_lead_min) setReminderLead(s.reminder_lead_min);
      } catch (e) {
        toast.error(e?.friendlyMessage || t("studio.open_scheduled_fail", "Impossible d'ouvrir le studio pour ce direct programmé."));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledId]);

  // Keep the live's title/description/offerings in sync when edited after creation.
  // « Diffuser en tant que » aussi : la clé OBS est créée AVANT le choix de l'église → sans ça le direct
  // restait sur la page du prédicateur.
  useEffect(() => {
    if (!live) return undefined;
    const id = setTimeout(() => {
      api.patch(`/live/${live.id}`, {
        title: form.title.trim(),
        description: form.description,
        accept_offerings: form.accept_offerings && canOffer,
        offering_goal: (form.accept_offerings && canOffer && Number(form.offering_goal) > 0) ? Number(form.offering_goal) : null,
        church_id: churchId || undefined,
        as_self: !churchId,
        ...(thumbUrl ? { thumbnail_url: thumbUrl } : {}),
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title, form.description, form.accept_offerings, form.offering_goal, live, thumbUrl, churchId]);

  // WHIP publish the local stream to Cloudflare
  const startWhip = async (whipUrl) => {
    const pc = new RTCPeerConnection();  // Cloudflare examples use no custom STUN
    pcRef.current = pc;
    pc.onconnectionstatechange = () => { if (pc.connectionState === "failed") toast.error(t("studio.server_lost", "Connexion au serveur perdue — réessaie.")); };
    streamRef.current.getTracks().forEach((t) => pc.addTrack(t, streamRef.current));
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await waitIce(pc);
    const resp = await fetch(whipUrl, { method: "POST", headers: { "Content-Type": "application/sdp" }, body: pc.localDescription.sdp });
    const answer = await resp.text();
    if (!resp.ok || !answer.trim()) throw new Error(`WHIP ${resp.status}`);
    const loc = resp.headers.get("Location");
    if (loc) { try { whipResourceRef.current = new URL(loc, whipUrl).href; } catch { whipResourceRef.current = loc; } }
    await pc.setRemoteDescription({ type: "answer", sdp: answer });
  };

  // --- Local replay recording -------------------------------------------------
  // Cloudflare does NOT record browser (WHIP/WebRTC) lives, so we record the
  // broadcast locally and upload it to Cloudflare Stream when the live ends.
  // Mobile is captured into a portrait canvas so the replay is a true vertical Reel.
  const buildRecordingStream = () => {
    // Record the ACTUAL published stream directly. On Android Chrome a
    // canvas.captureStream() + re-added audio track very often yields ZERO
    // MediaRecorder data (silent failure) → no replay, no message. Recording the
    // native camera stream is far more reliable; a phone held vertically already
    // produces a portrait video. Desktop keeps its native (landscape) stream too.
    const src = (isMobile && rawStreamRef.current) ? rawStreamRef.current : streamRef.current;
    if (!src) return null;
    const st = src.getVideoTracks()[0]?.getSettings?.() || {};
    if (st.width && st.height) recDimsRef.current = { width: st.width, height: st.height };
    return src;
  };

  const startReplayRecording = () => {
    recAttemptedRef.current = true;
    if (!window.MediaRecorder || !streamRef.current || recorderRef.current) return;
    const recStream = buildRecordingStream();
    if (!recStream) return;
    if (!recDimsRef.current || !recDimsRef.current.width) {
      const st = recStream.getVideoTracks()[0]?.getSettings?.() || {};
      recDimsRef.current = { width: st.width, height: st.height };
    }
    // vp8 first: broadest Android Chrome MediaRecorder support. NO custom
    // videoBitsPerSecond — setting it makes some Android builds emit no data.
    const candidates = ["video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
    let mime = "";
    for (const m of candidates) { try { if (MediaRecorder.isTypeSupported(m)) { mime = m; break; } } catch { /* noop */ } }
    recMimeRef.current = mime || "video/webm";
    let rec = null;
    try { rec = mime ? new MediaRecorder(recStream, { mimeType: mime }) : new MediaRecorder(recStream); }
    catch { try { rec = new MediaRecorder(recStream); } catch { rec = null; } }
    if (!rec) return; // recording unsupported — finalizeReplay will surface a message
    recordedChunksRef.current = [];
    diskWriterRef.current = null;
    // On native, stream chunks straight to a cache file (no OOM on long lives).
    // If the disk can't be written (full), fall back to RAM and warn the host.
    if (isNativePlatform()) {
      const ext = (recMimeRef.current || "").includes("mp4") ? "mp4" : "webm";
      createReplayFileWriter(`live-${liveRef.current?.id || Date.now()}.${ext}`)
        .then((w) => {
          if (w) diskWriterRef.current = w;
          else toast.info(t("studio.storage_low", "Espace de stockage limité — garde l'app ouverte pendant l'envoi de la rediffusion."));
        })
        .catch(() => {});
    }
    rec.ondataavailable = (e) => {
      if (!e.data || !e.data.size) return;
      const w = diskWriterRef.current;
      if (w && w.healthy()) { w.push(e.data).catch(() => {}); }
      else { recordedChunksRef.current.push(e.data); }
    };
    try { rec.start(1000); } catch { return; }
    recStartRef.current = Date.now();
    recorderRef.current = rec;
    setRecording(true);
  };

  const finalizeReplay = async () => {
    setRecording(false);
    const rec = recorderRef.current;
    recorderRef.current = null;
    if (rec && rec.state !== "inactive") {
      await new Promise((res) => { rec.onstop = res; try { rec.stop(); } catch { res(); } });
    }
    try { recCanvasCleanupRef.current?.(); } catch { /* noop */ }
    recCanvasCleanupRef.current = null;
    if (!liveRef.current?.id) { diskWriterRef.current = null; recordedChunksRef.current = []; return; }
    const mime = recMimeRef.current || "video/webm";
    const ext = mime.includes("mp4") ? "mp4" : "webm";
    const durationSec = recStartRef.current ? Math.max(1, Math.round((Date.now() - recStartRef.current) / 1000)) : 0;

    // ---- NATIVE: upload the on-disk file in the BACKGROUND -------------------
    // The @capgo native uploader runs a foreground service, so the send keeps
    // going when the app is backgrounded / the screen is locked. R2 → the server
    // transcodes to HLS + builds a poster (publish-recording). File deleted after.
    if (isNativePlatform() && diskWriterRef.current) {
      const w = diskWriterRef.current;
      diskWriterRef.current = null;
      const fin = await w.finish().catch(() => null);
      if (fin && fin.uri && (!fin.size || fin.size >= 200000)) {
        const dims = recDimsRef.current || {};
        let outW = dims.width || 0, outH = dims.height || 0;
        if (isMobile && outW && outH && outW > outH) { const t = outW; outW = outH; outH = t; }
        setRU({ active: true, progress: 0, phase: "upload" });
        let lastErr = null;
        for (let attempt = 0; attempt < 2 && liveRef.current?.id; attempt += 1) {
          try {
            const res = await uploadVideoNativeBackground({
              path: fin.uri, name: `live-${liveRef.current.id}.${ext}`, size: fin.size || 0,
              mimeType: mime, width: outW, height: outH, duration: durationSec, context: "feed", config,
              onProgress: (p) => setRU((s) => ({ ...s, progress: p, phase: "upload" })),
            });
            setRU((s) => ({ ...s, phase: "process", progress: 100 }));
            await api.post(`/live/${liveRef.current.id}/publish-recording`, {
              video_url: res.url, width: outW || res.width, height: outH || res.height, duration: durationSec || res.duration,
            });
            toast.success(t("studio.replay_ready", "Ta rediffusion est disponible 🎬"));
            lastErr = null;
            break;
          } catch (e) { lastErr = e; }
        }
        await w.remove().catch(() => {}); // free disk after success OR final failure
        if (lastErr) toast.error(t("studio.replay_send_fail", "La rediffusion n'a pas pu être envoyée. Vérifie ta connexion et réessaie."));
        setRU({ active: false, progress: 0, phase: null });
        recordedChunksRef.current = [];
        return;
      }
      // Unusable file (disk write failed early) → drop it and fall back to RAM below.
      await w.remove().catch(() => {});
    }

    // ---- WEB (or native disk fallback): RAM blob → Cloudflare Stream --------
    const chunks = recordedChunksRef.current || [];
    recordedChunksRef.current = [];
    if (!chunks.length) {
      if (recAttemptedRef.current) toast.error(t("studio.replay_record_fail_device", "La rediffusion n'a pas pu être enregistrée sur cet appareil."));
      return;
    }
    const blob = new Blob(chunks, { type: mime });
    if (blob.size < 200000) { toast.info(t("studio.replay_too_short", "Direct trop court pour créer une rediffusion.")); return; }
    // Read the real encoded dimensions from the recorded file.
    let mW = 0, mH = 0;
    try {
      const meta = await new Promise((resolve) => {
        const vEl = document.createElement("video");
        vEl.preload = "metadata"; vEl.muted = true;
        const url = URL.createObjectURL(blob);
        const done = (r) => { try { URL.revokeObjectURL(url); } catch { /* noop */ } resolve(r); };
        vEl.onloadedmetadata = () => done({ w: vEl.videoWidth, h: vEl.videoHeight });
        vEl.onerror = () => done({});
        setTimeout(() => done({}), 3000);
        vEl.src = url;
      });
      mW = meta.w || 0; mH = meta.h || 0;
    } catch { /* noop */ }
    const dims = recDimsRef.current || {};
    let outW = mW || dims.width || 0;
    let outH = mH || dims.height || 0;
    if (isMobile && outW && outH && outW > outH) { const tmp = outW; outW = outH; outH = tmp; }
    const file = new File([blob], `live-${liveRef.current.id}.${ext}`, { type: mime });
    setRU({ active: true, progress: 0, phase: "upload" });
    let lastErr = null;
    for (let attempt = 0; attempt < 2 && liveRef.current?.id; attempt += 1) {
      try {
        setRU({ active: true, progress: 0, phase: "upload" });
        // Web replay now lands on R2 (like feed videos); the server then builds a
        // poster and transcodes to adaptive HLS via the VOD provider (Bunny when
        // enabled, else Cloudflare). Replaces the old direct Cloudflare Stream upload.
        const res = await uploadMediaFile(file, { context: "feed", config, onProgress: (p) => setRU((s) => ({ ...s, progress: p, phase: "upload" })) });
        setRU((s) => ({ ...s, phase: "process", progress: 100 }));
        await api.post(`/live/${liveRef.current.id}/publish-recording`, {
          video_url: res.url,
          width: outW,
          height: outH,
          duration: durationSec,
        });
        toast.success(t("studio.replay_ready", "Ta rediffusion est disponible 🎬"));
        lastErr = null;
        break;
      } catch (e) {
        lastErr = e;
      }
    }
    if (lastErr) toast.error(t("studio.replay_record_fail", "La rediffusion n'a pas pu être enregistrée. Vérifie ta connexion et réessaie."));
    setRU({ active: false, progress: 0, phase: null });
  };

  const goLive = async () => {
    if (nativeRtmpRef.current) { if (!nativeRtmpReady) return toast.error(t("studio.cam_not_ready", "La caméra n'est pas encore prête.")); }
    else if (isBrowserSource && !streamRef.current) return toast.error(t("studio.allow_cam_first", "Autorise ta caméra ou ton écran d'abord"));
    else if (!isBrowserSource && !pb.cf_live) return toast.error(t("studio.obs_not_received", "Le flux OBS n'est pas encore reçu — démarre le streaming dans OBS."));
    setStarting(true);
    try {
      const l = await ensureLive();
      if (!l) return;
      if (nativeRtmpRef.current) {
        // Native RTMPS → Cloudflare (server-records the replay, no client upload).
        if (!l.rtmps_url) throw new Error("URL RTMPS indisponible");
        await startRtmpBroadcast({ url: l.rtmps_url, key: l.rtmps_key || "" });
        setNativeStreamStatus("connecting");
        // Cloudflare detects the incoming RTMPS flux and flips the status to LIVE
        // (HLS poll); go_live marks the host's intent so followers get notified.
        try { await api.patch(`/live/${l.id}`, { ingest: "rtmps", go_live: true }); } catch { /* noop */ }
      } else if (isBrowserSource) {
        if (!l.webrtc_url) throw new Error("URL WebRTC indisponible");
        await startWhip(l.webrtc_url);
        // A WHIP (WebRTC) stream produces NO HLS manifest, so Cloudflare-side
        // detection can't flip our status — tell the server directly. Viewers
        // will then watch via WHEP (WebRTC playback).
        try { await api.patch(`/live/${l.id}`, { ingest: "webrtc", go_live: true }); } catch { /* noop */ }
      } else {
        // OBS: the flux is already flowing (preview). Publish now → notify followers.
        await api.patch(`/live/${l.id}`, { ingest: "rtmps", go_live: true });
      }
      setPhase("live");
      // Replay strategy (guaranteed for everyone):
      //  • Browser WHIP (webcam/screen on desktop): record LOCALLY and upload at the end —
      //    Cloudflare does NOT reliably record WHIP, so the device recording is the guarantee.
      //  • Native mobile (RTMPS) and OBS (RTMPS): recorded SERVER-SIDE by Cloudflare and
      //    backfilled by the API (the app never sees the OBS video, so this is the only way).
      if (!nativeRtmpRef.current && isBrowserSource) startReplayRecording();
      toast.success(t("studio.now_live", "Tu es en direct ! 🔴 Tes abonnés sont prévenus."));
    } catch (e) {
      toast.error(e?.friendlyMessage || t("studio.start_fail", "Impossible de lancer le direct. Réessaie."));
    } finally { setStarting(false); }
  };

  const shareLive = async () => {
    const id = liveRef.current?.id;
    if (!id) return;
    const url = `${publicOrigin()}/live?watch=${id}`;
    await shareLinkCard({ title: form.title || t("studio.default_title", "Direct Yalola"), text: form.title || t("studio.default_title", "Direct Yalola"), url, copiedMessage: t("studio.link_copied", "Lien du direct copié 🔗") });
  };

  const endLive = async () => {
    setEnding(true);
    setEnded(true);
    // Native RTMPS: stop the encoder + free the camera (replay is recorded server-side).
    if (nativeRtmpRef.current) {
      nativeStatusRef.current = "idle"; reconnectSinceRef.current = 0;
      setNativeStreamStatus("idle");
      try { await stopRtmpBroadcast(); } catch { /* noop */ }
      try { await stopNativePreview(); } catch { /* noop */ }
    }
    try { if (liveRef.current) await api.post(`/live/${liveRef.current.id}/stop`); } catch { /* noop */ }
    toast.success(t("studio.ended_toast", "Direct terminé 🎬"));
    try { if (whipResourceRef.current) fetch(whipResourceRef.current, { method: "DELETE" }).catch(() => {}); } catch { /* noop */ }
    try { pcRef.current?.close(); } catch { /* noop */ }
    stopLocal();
    setEnding(false);
    // Browser (WHIP) lives aren't recorded by Cloudflare → upload the local recording
    // as the replay. This runs in the BACKGROUND (never blocks the host): a small pill
    // shows progress, the host can close right away, and on native the upload keeps
    // running via a foreground service. The studio auto-closes when it's done.
    if (recAttemptedRef.current) {
      finalizeReplay().catch(() => {}).finally(() => { closeOnce(); });
    } else {
      closeOnce();
    }
  };

  // Explicit confirmation so the host stops the broadcast properly (instead of just closing OBS).
  // Uses a styled in-app dialog (rendered below) — NOT the native window.confirm.
  const confirmEndLive = () => {
    if (phase === "live") { setEndConfirm(true); return; }
    endLive();
  };

  const closeStudio = async () => {
    // Only end/stop when we're actually broadcasting (phase === "live"). Closing the
    // studio while still in setup — e.g. a SCHEDULED live where we just opened it / copied
    // the OBS keys — must NOT end the live nor show "Direct terminé": the scheduled live
    // stays exactly as it was. We just release local media (+ any half-opened WHIP).
    if (phase === "live") return endLive();
    if (nativeRtmpRef.current) { try { await stopNativePreview(); } catch { /* noop */ } }
    try { if (whipResourceRef.current) fetch(whipResourceRef.current, { method: "DELETE" }).catch(() => {}); } catch { /* noop */ }
    try { pcRef.current?.close(); } catch { /* noop */ }
    stopLocal();
    closeOnce();
  };

  // Warn the host before closing the tab / navigating away WHILE actually broadcasting,
  // so they end the live properly instead of leaving a ghost stream running.
  useEffect(() => {
    if (phase !== "live") return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [phase]);


  // Poll Cloudflare playback once the input exists
  useEffect(() => {
    if (!live?.id) return;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/live/${live.id}/playback`);
        if (stop) return;
        setPb((prev) => ({ ...prev, ...data }));
        // Cross-worker viewer count fallback: the DB total is authoritative and
        // survives multi-worker splits, so the host never gets stuck at 0 even if
        // the WS 'viewer-count' push lands on another worker.
        if (typeof data.viewers === "number") setViewerCount(data.viewers);
        if (data.status === "live" && phase !== "live") { setPhase("live"); }
      } catch { /* noop */ }
    };
    poll();
    const iv = setInterval(() => { if (pb.status !== "ended") poll(); }, 4000);
    return () => { stop = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.id, phase, source]);

  // Aperçu HLS de l'hôte : compte les arrêts de lecture (waiting/stalled) pour signaler, en INFO neutre,
  // que l'aperçu charge par moments. Cela mesure la réception sur cet appareil (Cloudflare → navigateur),
  // PAS le débit montant OBS → l'alerte « Connexion OBS instable » repose sur /live/{id}/ingest (ci-dessous).
  // Les 30 premières secondes (playlist encore courte au démarrage) sont ignorées.
  useEffect(() => {
    const active = source === "obs" && pb.cf_live && !!pb.hls_url;
    if (!active) { setPreviewBuffering(false); netStallsRef.current = []; return undefined; }
    const v = obsHlsRef.current;
    if (!v) return undefined;
    const startedAt = Date.now();
    let stalledSince = 0;
    const now = () => Date.now();
    const evaluate = () => {
      const t = now();
      netStallsRef.current = netStallsRef.current.filter((x) => t - x < 15000);
      if (t - startedAt < 30000) { setPreviewBuffering(false); return; }
      const longStall = stalledSince && (t - stalledSince > 4000);
      setPreviewBuffering(netStallsRef.current.length >= 3 || !!longStall);
    };
    const onWaiting = () => { netStallsRef.current.push(now()); if (!stalledSince) stalledSince = now(); evaluate(); };
    const onPlaying = () => { stalledSince = 0; evaluate(); };
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("stalled", onWaiting);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("canplay", onPlaying);
    const iv = setInterval(evaluate, 2000);
    return () => {
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("stalled", onWaiting);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("canplay", onPlaying);
      clearInterval(iv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, pb.cf_live, pb.hls_url, phase]);

  // Liaison OBS → Cloudflare : interrogée toutes les 5 s tant que Cloudflare reçoit le flux (aperçu ou direct).
  useEffect(() => {
    if (!(source === "obs" && pb.cf_live && live?.id)) { setIngest(null); return undefined; }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/live/${live.id}/ingest`);
        if (!stop) setIngest(data?.available ? data : null);
      } catch { /* noop */ }
    };
    poll();
    const iv = setInterval(poll, 5000);
    return () => { stop = true; clearInterval(iv); };
  }, [source, pb.cf_live, live?.id]);

  // WS chat + reactions once we have a live id — lot 600 : reconnexion automatique après une coupure réseau.
  useEffect(() => {
    if (!live?.id) return;
    const sock = openLiveSocket(() => `${WS_BASE}/api/ws/live/${live.id}?token=${getToken()}&role=host`, {
      onMessage: (msg) => {
        if (msg.type === "viewer-count") setViewerCount(msg.count);
        else if (msg.type === "reaction") pushFloat(msg.emoji);
        else if (msg.type === "chat") setComments((c) => [...c.slice(-99), { id: Date.now() + Math.random(), userId: msg.user_id, name: msg.name, text: msg.text, isHost: msg.is_host }]);
        else if (msg.type === "offering") setPb((prev) => ({ ...prev, offering_total: msg.offering_total != null ? Number(msg.offering_total) : Number(prev.offering_total || 0) + Number(msg.amount || 0) }));
      },
    });
    wsRef.current = sock;
    return () => { sock.close(); wsRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.id]);

  useEffect(() => { commentsEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [comments]);
  const sendComment = () => { const t = commentText.trim(); if (!t || !wsRef.current?.send({ type: "chat", text: t })) return; setCommentText(""); };
  const sendReaction = (emoji) => { wsRef.current?.send({ type: "reaction", emoji }); };

  const canGoLive = nativeRtmp ? nativeRtmpReady : (isBrowserSource ? hasStream : !!pb.cf_live);
  // Lot 594 — l'écran du téléphone / PC ne doit jamais s'éteindre pendant qu'on prépare ou qu'on diffuse un direct.
  useScreenAwake(phase === "live" || hasStream || nativeRtmpReady, "live-host");
  const gateOk = !elig ? true : !!elig.can_go_live;
  // CTA follows the REAL cause (server `denial.cta`): badge → Yalola Vérifié, identity → KYC card.
  const gateCta = (cta, testId) => cta?.kind ? (
    <Button size="sm" className="pill-primary rounded-full mt-2" onClick={() => navigate(cta.url)} data-testid={testId} data-cta={cta.kind}>
      {cta.kind === "badge" ? t("live.gate_badge_cta", "Activer Yalola Vérifié") : t("live.gate_identity_cta", "Vérifier mon identité")}
    </Button>
  ) : null;
  const gateBanner = (elig && !elig.can_go_live) ? (
    <div className="rounded-2xl px-4 py-3" style={{ background: "rgba(217,119,87,0.14)", border: "1px solid rgba(217,119,87,0.5)" }} data-testid="live-gate-hint" data-denial={elig.denial?.code || ""}>
      {elig.blocked ? (
        <>
          <p className="text-sm text-white/90">{elig.denial?.message || t("studio.gate_blocked", "Ton type de compte ne permet pas encore de passer en direct.")}</p>
          {gateCta(elig.denial?.cta, "live-gate-cta")}
        </>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-white" data-testid="live-gate-remaining" data-n={elig.followers_needed}>{t("studio.gate_more", "Encore {n} abonné(s) pour passer en direct").replace("{n}", elig.followers_needed)}</span>
            <span className="text-xs text-white/70">{elig.current_followers}/{elig.min_followers}</span>
          </div>
          <div className="mt-2 h-1.5 w-full rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.18)" }}>
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, Math.round((elig.current_followers / Math.max(1, elig.min_followers)) * 100))}%`, background: "#D97757" }} />
          </div>
          <p className="mt-1.5 text-[11px] text-white/60">{t("studio.gate_realtime", "Le compteur se met à jour en temps réel dès que de nouveaux abonnés te rejoignent.")}</p>
          {gateCta(elig.denial?.cta, "live-gate-cta")}
        </>
      )}
    </div>
  ) : null;
  // Creator allowed to go live but not (yet) to receive offerings: say exactly what is missing.
  const offeringsHint = (elig?.offerings_denial && ["preacher", "artist", "influencer"].includes(user?.role)) ? (
    <div className="rounded-2xl px-4 py-3" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)" }} data-testid="live-offerings-hint" data-denial={elig.offerings_denial.code}>
      <p className="text-sm text-white/85">{elig.offerings_denial.message}</p>
      <div className="flex gap-2 flex-wrap">
        {gateCta(elig.offerings_denial.cta, "live-offerings-cta")}
        {elig.offerings_denial.secondary_cta && (
          <Button size="sm" variant="outline" className="rounded-full mt-2 text-white border-white/40" onClick={() => navigate(elig.offerings_denial.secondary_cta.url)} data-testid="live-offerings-cta-secondary" data-cta={elig.offerings_denial.secondary_cta.kind}>
            {elig.offerings_denial.secondary_cta.kind === "badge" ? t("live.gate_badge_cta", "Activer Yalola Vérifié") : t("live.gate_identity_cta", "Vérifier mon identité")}
          </Button>
        )}
      </div>
    </div>
  ) : null;

  // Live time-limit countdown + 5-min auto-stop warning (limits from Admin → Directs).
  useEffect(() => {
    if (phase !== "live") return undefined;
    const iv = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [phase]);
  const maxMin = pb.max_duration_min || 0;
  const liveStartMs = pb.live_started_at ? new Date(pb.live_started_at).getTime() : null;
  const hasLimit = phase === "live" && maxMin > 0 && !!liveStartMs;
  const remainingSec = hasLimit ? Math.max(0, Math.round(maxMin * 60 - (nowTs - liveStartMs) / 1000)) : 0;
  const timeUp = hasLimit && remainingSec <= 0;
  // Lot 595 — batterie faible pendant le direct : bannière + vibration à 15 %, 10 % et 5 % ; disparaît quand on branche.
  const battery = useLiveBatteryAlert(phase === "live" && !ended);
  const warn5 = hasLimit && remainingSec > 0 && remainingSec <= 300;
  const fmtMMSS = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const mirror = source === "webcam" && facing === "user";

  // Hard cap: when the live hits its max duration, stop it on THIS device immediately
  // (frees the phone camera/encoder) — the backend scheduler also enforces it server-side.
  useEffect(() => {
    if (!timeUp || autoStoppedRef.current || phase !== "live" || ended) return;
    autoStoppedRef.current = true;
    toast.message(t("studio.time_up_auto", "Durée maximale atteinte — arrêt automatique du direct."));
    endLive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeUp, phase, ended]);

  // Lot 600 — le serveur a clos le direct (fenêtre de reprise dépassée, arrêt automatique…) pendant que
  // l'hôte est encore dans le studio : on libère la caméra et on affiche l'écran « terminé » nous-mêmes.
  useEffect(() => {
    if (pb.status !== "ended" || autoStoppedRef.current || phase !== "live" || ended) return;
    autoStoppedRef.current = true;
    if (nativeStatusRef.current === "reconnecting") toast.error(t("live.connection_lost", "Connexion au serveur perdue."));
    endLive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pb.status, phase, ended]);

  // Native broadcast network-quality badge (derived from the plugin's bitrate).
  const netQuality = (() => {
    if (nativeStreamStatus === "connecting" || nativeBitrate <= 0) return { label: t("live.net_connecting", "Connexion…"), color: "#9CA3AF" };
    if (nativeBitrate >= 1500) return { label: t("live.net_good", "Bon"), color: "#22C55E" };
    if (nativeBitrate >= 600) return { label: t("live.net_medium", "Moyen"), color: "#F59E0B" };
    return { label: t("live.net_weak", "Faible"), color: "#EF4444" };
  })();

  // TikTok/Facebook-style mobile studio: camera opens full-screen, one tap to go live.
  // Mobile WEB (not the installed app): browser camera live was removed. Prompt the
  // user to install the app (camera → native RTMPS) or use OBS on a computer.
  const renderMobileInstall = () => {
    const prompt = getInstallPrompt();
    const ios = isIOS();
    return (
      <div className="absolute inset-0 bg-black flex flex-col items-center justify-center text-center px-8" data-testid="studio-install-app" style={{ color: "#EAF2EE" }}>
        <button onClick={closeStudio} className="absolute h-10 w-10 rounded-full grid place-items-center" style={{ top: "calc(var(--sa-top) + 0.6rem)", left: 12, background: "rgba(255,255,255,0.1)", color: "#fff" }} data-testid="studio-close-btn"><X size={20} /></button>
        <span className="h-20 w-20 rounded-3xl grid place-items-center mb-5" style={{ background: "rgba(217,119,87,0.18)" }}>
          <Smartphone size={38} style={{ color: "#D97757" }} />
        </span>
        <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>{t("studio.mobile_only_title", "Filme ton direct depuis l'application")}</h2>
        <p className="text-sm max-w-xs" style={{ color: "#A0B0AA" }}>
          {t("studio.mobile_only_body", "La diffusion caméra en direct est disponible sur l'application Yalola. Installe-la pour filmer depuis ton téléphone — ou utilise {obs} depuis un ordinateur.").split("{obs}").map((part, i, arr) => <React.Fragment key={i}>{part}{i < arr.length - 1 && <b>OBS Studio</b>}</React.Fragment>)}
        </p>
        {prompt && (
          <Button onClick={() => { try { prompt.prompt(); } catch { /* noop */ } }} className="rounded-full mt-6 h-12 px-6 font-semibold" style={{ background: "#D97757", color: "#fff" }} data-testid="studio-install-cta">
            <Download size={18} className="mr-2" /> {t("studio.install_app", "Installer l'application")}
          </Button>
        )}
        {ios && (
          <p className="text-xs mt-5 max-w-xs" style={{ color: "#7BA697" }}>
            {t("studio.ios_install_hint", "Sur iPhone : appuie sur {share}, puis « {add} » pour installer Yalola.").split(/(\{share\}|\{add\})/).map((part, i) => part === "{share}" ? <b key={i}>{t("studio.ios_share", "Partager")}</b> : part === "{add}" ? <b key={i}>{t("studio.ios_add_home", "Sur l'écran d'accueil")}</b> : part)}
          </p>
        )}
      </div>
    );
  };

  const renderMobile = () => (
    <div className={`absolute inset-0 flex flex-col${nativeRtmp ? "" : " bg-black"}`} data-testid="studio-mobile">
      <div className="absolute inset-0">
        {nativeRtmp ? (
          (nativeRtmpReady && !nativeRtmpErr && !ended) ? null : (
            <div className="w-full h-full grid place-items-center text-center px-8" style={{ color: "#A0B0AA" }}>
              {ended ? (
                <div className="space-y-3" data-testid="studio-ended">
                  <div className="text-4xl">🎬</div>
                  <p className="text-base font-semibold" style={{ color: "#EAF2EE" }}>{t("studio.ended", "Direct terminé")}</p>
                  <p className="text-sm">{t("studio.ended_replay_soon", "Merci ! Ta rediffusion est enregistrée automatiquement et sera bientôt disponible.")}</p>
                </div>
              ) : nativeRtmpErr ? (
                <div className="space-y-3" data-testid="cam-error">
                  <VideoOff size={40} className="mx-auto" />
                  <p className="text-sm">{nativeRtmpErr}</p>
                  <Button onClick={startNativePreview} className="rounded-full" style={{ background: "#2D5A4C", color: "#fff" }} data-testid="mobile-cam-retry">{t("studio.retry", "Réessayer")}</Button>
                </div>
              ) : (
                <div className="space-y-3"><Loader2 size={34} className="mx-auto animate-spin" /><p className="text-sm">{t("studio.opening_cam", "Ouverture de la caméra…")}</p></div>
              )}
            </div>
          )
        ) : streamRef.current ? (
          <video
            ref={videoRef}
            autoPlay playsInline muted
            className="w-full h-full object-cover"
            style={mirror ? { transform: "scaleX(-1)" } : undefined}
            data-testid="live-preview-video"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-center px-8" style={{ color: "#A0B0AA" }}>
            {ended ? (
              <div className="space-y-3" data-testid="studio-ended">
                <div className="text-4xl">🎬</div>
                <p className="text-base font-semibold" style={{ color: "#EAF2EE" }}>{t("studio.ended", "Direct terminé")}</p>
                {recAttemptedRef.current ? (
                  <p className="text-sm">{t("studio.ended_upload_bg", "La caméra est arrêtée. L'envoi de ta rediffusion se poursuit en arrière-plan — tu peux fermer.")}</p>
                ) : (
                  <p className="text-sm">{t("studio.ended_no_replay", "Merci ! Ton direct est terminé. (Aucune rediffusion enregistrée)")}</p>
                )}
              </div>
            ) : camError ? (
              <div className="space-y-3" data-testid="cam-error">
                <VideoOff size={40} className="mx-auto" />
                <p className="text-sm">{camError}</p>
                <Button onClick={() => initMobileCam(facing)} className="rounded-full" style={{ background: "#2D5A4C", color: "#fff" }} data-testid="mobile-cam-retry">{t("studio.retry", "Réessayer")}</Button>
              </div>
            ) : (
              <div className="space-y-3"><Loader2 size={34} className="mx-auto animate-spin" /><p className="text-sm">{t("studio.opening_cam", "Ouverture de la caméra…")}</p></div>
            )}
          </div>
        )}
      </div>

      {/* Legibility gradients */}
      <div className="absolute inset-x-0 top-0 h-28 pointer-events-none z-10" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.55), transparent)" }} />
      <div className="absolute inset-x-0 bottom-0 h-64 pointer-events-none z-10" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.72), transparent)" }} />

      {/* Lot 643 — zone « avant / après » : doigt maintenu sur l'aperçu = image sans lissage (entre la barre du haut et les commandes) */}
      {compareAvailable && (
        <div
          className="absolute inset-x-0 z-20 select-none"
          style={{ top: "calc(var(--sa-top) + 3.6rem)", bottom: phase === "setup" ? "17rem" : "13rem", touchAction: "none", WebkitTouchCallout: "none", WebkitUserSelect: "none" }}
          onPointerDown={startCompare} onPointerUp={stopCompare} onPointerCancel={stopCompare} onPointerLeave={stopCompare}
          onContextMenu={(e) => e.preventDefault()}
          data-testid="live-beauty-compare-zone" data-comparing={comparing ? 1 : 0}
        >
          {comparing && (
            <div className="absolute left-1/2 -translate-x-1/2 top-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white pointer-events-none" style={{ background: "rgba(0,0,0,0.6)", border: "1px solid rgba(255,255,255,0.25)" }} data-testid="live-beauty-compare-pill">
              <EyeOff size={14} /> {t("studio.beauty_compare_off", "Sans lissage")}
            </div>
          )}
        </div>
      )}

      {phase === "live" && <FloatingReactions floats={floats} />}

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-3" style={{ paddingTop: "calc(var(--sa-top) + 0.6rem)" }}>
        <div className="flex items-center gap-2">
          <button onClick={closeStudio} className="h-10 w-10 rounded-full grid place-items-center" style={{ background: "rgba(0,0,0,0.5)", color: "#fff" }} data-testid="studio-close-btn"><X size={20} /></button>
          {fxAvailable && !ended && (
            <button type="button" onClick={() => setFxOpen((o) => !o)} aria-expanded={fxOpen} className="h-10 px-3 rounded-full flex items-center gap-1" style={{ background: fxOpen || liveFx !== "normal" ? "#D97757" : "rgba(0,0,0,0.5)", color: "#fff" }} data-testid="live-fx-btn" data-on={liveFx !== "normal" ? 1 : 0} data-open={fxOpen ? 1 : 0} title={t("studio.filters", "Filtres")}>
              <Wand2 size={16} /> <span className="text-[11px] font-semibold">{liveFx === "normal" ? t("studio.filters", "Filtres") : t(`fx.preset_${liveFx}`, FX_PRESETS.find((p) => p.key === liveFx)?.label || "")}</span>
            </button>
          )}
        </div>
        {phase === "live" ? (
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider" style={{ background: "#D97757", color: "#fff" }} data-testid="live-badge-live"><span className="h-2 w-2 rounded-full bg-white animate-pulse" /> {t("studio.live_badge", "En direct")}</span>
            {recording && <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-bold" style={{ background: "rgba(0,0,0,0.55)", color: "#fff" }} data-testid="live-rec-indicator"><span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> REC</span>}
            {nativeRtmp && !ended && <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-bold" style={{ background: "rgba(0,0,0,0.55)", color: "#fff" }} data-testid="live-recording-cloud" title={t("studio.recorded_title", "Ta rediffusion est enregistrée automatiquement")}><span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> {t("studio.recorded", "Enregistré")}</span>}
            <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs" style={{ background: "rgba(0,0,0,0.5)", color: "#fff" }} data-testid="live-viewer-count"><Users size={13} /> {viewerCount}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {nativeRtmp && (
              <button onClick={() => changeRtmpQuality(rtmpQuality === "720" ? "1080" : "720")} className="h-10 px-3 rounded-full flex items-center gap-1" style={{ background: "rgba(0,0,0,0.5)", color: "#fff" }} data-testid="mobile-quality-btn" title={t("studio.quality", "Qualité vidéo")}>
                <span className="text-[11px] font-semibold">{RTMP_QUALITIES[rtmpQuality].label}</span>
              </button>
            )}
            {beautyAvailable && (
              <button type="button" onClick={() => setBeautyOpen((o) => !o)} aria-expanded={beautyOpen} className="h-10 px-3 rounded-full flex items-center gap-1" style={{ background: beautyOpen || beauty > 0 ? "#D97757" : "rgba(0,0,0,0.5)", color: "#fff" }} data-testid="mobile-beauty-btn" data-level={beauty} data-open={beautyOpen ? 1 : 0} title={t("studio.beauty_filter", "Filtre beauté")}>
                <Sparkles size={16} /> <span className="text-[11px] font-semibold">{beauty === 0 ? t("studio.beauty_off", "Beauté") : `${beauty} %`}</span>
              </button>
            )}
            <button onClick={flipCamera} className="h-10 w-10 rounded-full grid place-items-center" style={{ background: "rgba(0,0,0,0.5)", color: "#fff" }} data-testid="mobile-flip-cam"><SwitchCamera size={18} /></button>
            <button onClick={toggleMic} className="h-10 w-10 rounded-full grid place-items-center" style={{ background: micOn ? "rgba(0,0,0,0.5)" : "#D97757", color: "#fff" }} data-testid="toggle-mic">{micOn ? <Mic size={18} /> : <MicOff size={18} />}</button>
          </div>
        )}
      </div>

      {/* 3·2·1 selfie countdown before going live */}
      {countdown !== null && (
        <button onClick={cancelCountdown} className="absolute inset-0 z-40 grid place-items-center" style={{ background: "rgba(0,0,0,0.45)" }} data-testid="live-countdown-overlay">
          <span key={countdown} className="text-white font-bold leading-none" style={{ fontSize: "7rem", animation: "cdpop 1s ease-out", fontFamily: "Outfit" }}>{countdown}</span>
          <span className="absolute bottom-24 text-white/80 text-sm">{t("studio.tap_cancel", "Touche pour annuler")}</span>
        </button>
      )}

      {phase === "live" && hasLimit && (
        <span className="absolute z-30 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ top: "calc(var(--sa-top) + 3.4rem)", background: (timeUp || warn5) ? "#EF4444" : "rgba(0,0,0,0.5)", color: "#fff" }} data-testid="studio-live-countdown">
          <Clock size={13} /> {timeUp ? "Temps écoulé" : fmtMMSS(remainingSec)}
        </span>
      )}

      {/* Native RTMPS: reconnecting banner — lot 600 : chrono + rappel que la reprise est automatique */}
      {phase === "live" && !ended && nativeRtmp && nativeStreamStatus === "reconnecting" && (
        <div className="absolute z-40 inset-x-0 flex flex-col items-center gap-1.5 px-4" style={{ top: "calc(var(--sa-top) + 3.4rem)" }} data-testid="live-reconnecting">
          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold shadow-lg" style={{ background: "#B45309", color: "#fff" }}>
            <Loader2 size={13} className="animate-spin" /> {t("live.reconnecting", "Reconnexion…")}
          </span>
          <span className="max-w-sm text-center px-3 py-1.5 rounded-xl text-[11px] font-medium leading-snug" style={{ background: "rgba(0,0,0,0.55)", color: "#fff" }} data-testid="live-reconnecting-hint">
            {t("live.reconnecting_hint", "Ne quitte pas : le direct reprend tout seul dès que le réseau revient ({mmss})").replace("{mmss}", fmtMMSS(reconnectSinceRef.current ? Math.max(0, Math.round((nowTs - reconnectSinceRef.current) / 1000)) : 0))}
          </span>
        </div>
      )}

      {/* Native RTMPS: live network-quality badge */}
      {phase === "live" && !ended && nativeRtmp && nativeStreamStatus !== "reconnecting" && (
        <span className="absolute z-30 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ top: "calc(var(--sa-top) + 3.4rem)", background: "rgba(0,0,0,0.5)", color: "#fff" }} data-testid="live-net-quality">
          <Wifi size={13} style={{ color: netQuality.color }} /> <span style={{ color: netQuality.color }}>{netQuality.label}</span>
        </span>
      )}

      {/* Native RTMPS: "weak network" banner — shown while the adaptive encoder is
          streaming below the target bitrate (quality was lowered to avoid cuts). */}
      {phase === "live" && !ended && nativeRtmp && nativeStreamStatus === "live" && lowNetwork && (
        <div className="absolute z-40 inset-x-0 flex justify-center px-4" style={{ top: `calc(var(--sa-top) + ${hasLimit && warn5 ? "9rem" : "6.2rem"})` }} data-testid="live-low-network">
          <span className="flex items-center gap-2 px-4 py-2 rounded-2xl text-xs font-semibold shadow-lg" style={{ background: "rgba(180,83,9,0.92)", color: "#fff" }}>
            <WifiOff size={14} /> {t("live.low_network", "Réseau faible — qualité réduite pour éviter les coupures")}
          </span>
        </div>
      )}

      {/* Prominent end-of-time alert (last 5 min → banner ; last 60 s → red + pulse) */}
      {phase === "live" && !ended && hasLimit && warn5 && (
        <div className="absolute z-40 inset-x-0 flex justify-center px-4" style={{ top: "calc(var(--sa-top) + 6.2rem)" }} data-testid="live-ending-soon">
          <div className="flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold shadow-lg" style={{ background: remainingSec <= 60 ? "#DC2626" : "#B45309", color: "#fff" }}>
            <Clock size={16} className={remainingSec <= 60 ? "animate-pulse" : ""} />
            {remainingSec <= 60 ? `Arrêt automatique dans ${remainingSec}s` : `Ton direct s'arrête dans ${fmtMMSS(remainingSec)}`}
          </div>
        </div>
      )}

      {/* Lot 595 — batterie faible pendant le direct : 15 % (ambre), 10 % / 5 % (rouge, pulsation) ; fermable ; revient au palier suivant. */}
      {phase === "live" && !ended && battery.show && (
        <div className="absolute z-40 inset-x-0 flex justify-center px-4" style={{ top: `calc(var(--sa-top) + ${hasLimit && warn5 ? "9rem" : "6.2rem"} + ${nativeRtmp && nativeStreamStatus === "live" && lowNetwork ? "2.8rem" : "0rem"})` }} data-testid="live-battery-low">
          <div className="flex items-center gap-2.5 pl-3.5 pr-2 py-2 rounded-2xl text-sm font-semibold shadow-lg" style={{ background: battery.step <= 0.10 ? "rgba(220,38,38,0.94)" : "rgba(180,83,9,0.94)", color: "#fff" }}>
            <BatteryLow size={18} className={battery.step <= 0.10 ? "animate-pulse" : ""} />
            <div className="leading-tight">
              <div data-testid="live-battery-low-title">{t("studio.battery_low_title", "Batterie faible : {pct} %").replace("{pct}", String(battery.pct ?? Math.round(battery.step * 100)))}</div>
              <div className="text-xs font-medium opacity-90" data-testid="live-battery-low-msg">{t("studio.battery_low_msg", "Branche ton téléphone pour ne pas couper le direct.")}</div>
            </div>
            <button type="button" onClick={battery.dismiss} className="ml-1 p-1.5 rounded-full hover:bg-white/15" aria-label={t("common.close", "Fermer")} data-testid="live-battery-low-close"><X size={14} /></button>
          </div>
        </div>
      )}

      {/* Bottom controls — on the native RTMP studio the camera view stops above the system
          bar (black strip), so the controls sit a little higher to stay on the picture. */}
      <div className="absolute bottom-0 inset-x-0 z-30 px-3" style={{ paddingBottom: `calc(var(--sa-bottom) + ${nativeRtmp ? "2.25rem" : "0.85rem"})` }}>
        {phase === "setup" ? (
          <div className="space-y-2.5">
            {beautyAvailable && beautyOpen && <LiveBeautySlider value={beauty} onChange={setBeautyLevel} t={t} />}
            {fxAvailable && fxOpen && <LiveFxStrip value={liveFx} onPick={pickLiveFx} thumbSrc={fxThumb} t={t} beauty={beauty} onBeauty={() => setBeautyOpen((o) => !o)} beautyEnabled={beautyAvailable} />}
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("studio.title_optional_ph", "Ajoute un titre (facultatif)…")} maxLength={120} className="w-full h-11 rounded-full px-4 text-sm text-white outline-none placeholder:text-white/60" style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.16)" }} data-testid="live-title-input" />
            {myChurches.length > 0 && (
              <div className="rounded-2xl px-3 py-2.5" style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.16)" }} data-testid="live-broadcast-as">
                <div className="text-[11px] font-semibold text-white/70 mb-1.5">{t("studio.broadcast_as", "Diffuser en tant que")}</div>
                {myChurches.length === 1 ? (
                  <div className="flex gap-1.5">
                    <button type="button" onClick={() => setChurchId("")} className="flex-1 h-9 rounded-full text-xs font-semibold transition-colors" style={{ background: churchId === "" ? "#fff" : "transparent", color: churchId === "" ? "#111" : "#fff", border: "1px solid rgba(255,255,255,0.3)" }} data-testid="live-as-self">{t("studio.me", "Moi")}</button>
                    <button type="button" onClick={() => setChurchId(myChurches[0].id)} className="flex-1 h-9 rounded-full text-xs font-semibold flex items-center justify-center gap-1 transition-colors" style={{ background: churchId === myChurches[0].id ? "#fff" : "transparent", color: churchId === myChurches[0].id ? "#111" : "#fff", border: "1px solid rgba(255,255,255,0.3)" }} data-testid="live-as-church"><Church size={13} /> {myChurches[0].name}</button>
                  </div>
                ) : (
                  <select value={churchId} onChange={(e) => setChurchId(e.target.value)} className="w-full h-9 rounded-full px-3 text-sm text-white outline-none bg-transparent" style={{ border: "1px solid rgba(255,255,255,0.3)" }} data-testid="live-church-select">
                    <option value="" style={{ color: "#111" }}>{t("studio.me_personal", "Moi (compte personnel)")}</option>
                    {myChurches.map((c) => <option key={c.id} value={c.id} style={{ color: "#111" }}>🏛️ {c.name}</option>)}
                  </select>
                )}
                {churchId && (sharedLookOf(churchId) || hasChurchLook(churchId)) && (
                  <div className="text-[10px] text-white/60 mt-1.5 flex items-center gap-1" data-testid="live-look-remembered" data-shared={sharedLookOf(churchId) ? 1 : 0}>
                    <Sparkles size={11} />
                    {sharedLookOf(churchId)
                      ? <span>{t("studio.look_shared", "Rendu partagé avec les gestionnaires de cette église")}{sharedLookOf(churchId).updated_by_name && sharedLookOf(churchId).updated_by !== user?.id ? ` · ${t("studio.look_shared_by", "Dernier réglage par {name}").replace("{name}", sharedLookOf(churchId).updated_by_name)}` : ""}</span>
                      : t("studio.look_remembered", "Filtre et lissage mémorisés pour cette église")}
                  </div>
                )}
              </div>
            )}
            {canOffer && (
              <button
                type="button"
                onClick={() => setForm({ ...form, accept_offerings: !form.accept_offerings })}
                className="w-full flex items-center justify-between rounded-full px-4 h-11"
                style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.16)" }}
                data-testid="mobile-toggle-offerings"
              >
                <span className="flex items-center gap-2 text-sm text-white"><Heart size={15} style={{ color: "#D97757" }} /> {offer.Plural} {form.accept_offerings ? offer.on : offer.off}</span>
                <span className="relative inline-block h-5 w-9 rounded-full transition-colors" style={{ background: form.accept_offerings ? "#2D5A4C" : "rgba(255,255,255,0.25)" }}>
                  <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all" style={{ left: form.accept_offerings ? "18px" : "2px" }} />
                </span>
              </button>
            )}
            {canOffer && form.accept_offerings && showGoal && (
              <div className="flex items-center gap-2 rounded-full px-4 h-11" style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.16)" }} data-testid="mobile-offering-goal-wrap">
                <Target size={15} style={{ color: "#D97757" }} />
                <span className="text-sm text-white/80 shrink-0">{t("studio.goal", "Objectif")}</span>
                <input type="number" min="0" inputMode="numeric" value={form.offering_goal} onChange={(e) => setForm({ ...form, offering_goal: e.target.value })} placeholder={t("studio.goal_optional_eur", "Facultatif (€)")} className="flex-1 min-w-0 bg-transparent text-sm text-white text-right outline-none placeholder:text-white/40" data-testid="mobile-offering-goal-input" />
                <span className="text-sm text-white/60">€</span>
              </div>
            )}
            {gateBanner}
            {offeringsHint}
            {isBrowserSource && (
              <p className="text-[11px] px-2 flex items-center gap-1.5" style={{ color: "rgba(255,255,255,0.6)" }} data-testid="mobile-replay-note">
                <Video size={13} style={{ color: "#E0A63C" }} /> {t("studio.replay_auto_note", "La rediffusion est enregistrée automatiquement.")}
              </p>
            )}
            <Button onClick={startCountdown} disabled={!canGoLive || starting || countdown !== null || !gateOk} className="w-full h-14 rounded-full font-bold text-base shadow-lg" style={{ background: canGoLive && !starting && gateOk ? "#D97757" : "rgba(255,255,255,0.22)", color: "#fff" }} data-testid="btn-lancer-direct">
              {starting ? <><Loader2 size={20} className="mr-2 animate-spin" /> {t("studio.starting", "Lancement…")}</> : <><Radio size={20} className="mr-2" /> {t("studio.go_live", "Passer en direct")}</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-2.5">
            {beautyAvailable && beautyOpen && !ended && <LiveBeautySlider value={beauty} onChange={setBeautyLevel} t={t} />}
            {fxAvailable && fxOpen && !ended && <LiveFxStrip value={liveFx} onPick={pickLiveFx} thumbSrc={fxThumb} t={t} beauty={beauty} onBeauty={() => setBeautyOpen((o) => !o)} beautyEnabled={beautyAvailable} />}
            <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1" data-testid="live-comments">
              {comments.length === 0 && <p className="text-xs text-white/70">{t("studio.first_cheer", "Sois le premier à encourager ! 🙏")}</p>}
              {comments.slice(-8).map((c) => (
                <div key={c.id} className="text-sm leading-snug drop-shadow"><span className="font-semibold" style={{ color: c.isHost ? "#7fdcb0" : "#fff" }}>{c.name}</span><span className="ml-1.5 text-white/90">{c.text}</span></div>
              ))}
              <div ref={commentsEndRef} />
            </div>
            {canOffer && form.accept_offerings && showGoal && offeringGoal > 0 && (
              <div className="rounded-xl px-3 py-2" style={{ background: "rgba(217,119,87,0.12)", border: "1px solid rgba(217,119,87,0.3)" }} data-testid="host-offering-goal-mobile">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="flex items-center gap-1.5 font-semibold text-white/90"><Target size={13} style={{ color: "#D97757" }} /> {offer.goal}</span>
                  <span className="font-bold" style={{ color: "#D97757" }} data-testid="host-offering-goal-amounts-mobile">{Math.round(Number(pb.offering_total || 0)).toLocaleString()} {curSym} / {Math.round(offeringGoal).toLocaleString()} {curSym}</span>
                </div>
                <div className="h-2 w-full rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.15)" }}>
                  <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${offeringPct}%`, background: "#D97757" }} data-testid="host-offering-goal-fill-mobile" />
                </div>
                {Number(pb.offering_total || 0) >= offeringGoal && (
                  <div className="text-[11px] mt-1.5 font-semibold" style={{ color: "#28C291" }}>🎉 {t("live.goal_reached", "Objectif atteint — gloire à Dieu !")}</div>
                )}
              </div>
            )}
            {canOffer && form.accept_offerings && (
              <button onClick={() => setRecapOpen(true)} className="w-full h-9 rounded-full text-xs font-semibold flex items-center justify-center gap-1.5" style={{ background: "rgba(217,119,87,0.92)", color: "#fff" }} data-testid="host-open-offering-recap-mobile">
                <Gift size={14} /> {offer.Plural} {offer.received} · {Math.round(Number(pb.offering_total || 0)).toLocaleString()} {curSym}
              </button>
            )}
            <div className="flex items-center gap-2 overflow-x-auto">
              {reactionEmojis.map((e, i) => (
                <button key={e} onClick={() => sendReaction(e)} className="h-9 w-9 shrink-0 rounded-full grid place-items-center text-lg transition-transform active:scale-90" style={{ background: "rgba(0,0,0,0.5)" }} data-testid={`live-react-btn-${i}`}>{e}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input className="flex-1 min-w-0 h-11 rounded-full px-4 text-sm outline-none text-white placeholder:text-white/60" style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.16)" }} value={commentText} onChange={(e) => setCommentText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendComment()} placeholder={t("studio.comment_ph", "Commenter…")} maxLength={500} data-testid="live-comment-input" />
              <button onClick={sendComment} className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: "#2D5A4C", color: "#fff" }} data-testid="live-comment-send"><Send size={16} /></button>
              <button onClick={confirmEndLive} disabled={ending} className="h-11 px-4 rounded-full grid place-items-center shrink-0 font-semibold text-sm" style={{ background: "#dc2626", color: "#fff" }} data-testid="btn-terminer-direct">{ending ? <Loader2 size={16} className="animate-spin" /> : t("studio.end_short", "Fin")}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // In-app "end the live?" confirmation. Rendered at the TOP LEVEL (below) so it
  // works on BOTH the mobile and desktop studio layouts — previously it lived only
  // inside renderMobile(), so on desktop clicking "Terminer le direct" set the flag
  // but no dialog appeared and the live could never be stopped.
  const endConfirmDialog = endConfirm ? (
    <div className="fixed inset-0 z-[95] grid place-items-center px-6" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} data-testid="end-live-confirm">
      <div className="w-full max-w-sm rounded-3xl p-6 text-center" style={{ background: "#14201B", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
        <div className="mx-auto mb-4 h-14 w-14 rounded-full grid place-items-center" style={{ background: "rgba(220,38,38,0.15)" }}>
          <Radio size={26} style={{ color: "#EF4444" }} />
        </div>
        <h3 className="text-white text-lg font-bold" style={{ fontFamily: "Outfit" }}>{t("studio.end_confirm_title", "Terminer le direct ?")}</h3>
        <p className="mt-2 text-sm text-white/70 leading-relaxed">{t("studio.end_confirm_body", "Le direct s'arrêtera et les spectateurs seront redirigés vers l'accueil. Cette action est définitive.")}</p>
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => setEndConfirm(false)}
            disabled={ending}
            className="flex-1 h-11 rounded-full font-semibold text-sm text-white transition-colors"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.28)" }}
            data-testid="end-live-cancel"
          >
            {t("studio.cancel", "Annuler")}
          </button>
          <button
            onClick={() => { setEndConfirm(false); endLive(); }}
            disabled={ending}
            className="flex-1 h-11 rounded-full font-semibold text-sm text-white inline-flex items-center justify-center transition-colors"
            style={{ background: "#dc2626" }}
            data-testid="end-live-confirm-btn"
          >
            {ending ? <Loader2 size={16} className="animate-spin" /> : "Terminer le direct"}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className={`fixed inset-0 z-[60] text-white${nativeRtmp ? " yalola-rtmp-studio" : ""}`} style={{ background: nativeRtmp ? "transparent" : "#0A0D0C", fontFamily: "Outfit, sans-serif" }} data-testid="studio-layout-container">
      {endConfirmDialog}
      {replayUpload.active && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-[90] w-[min(92vw,420px)] rounded-2xl px-4 py-3 shadow-2xl" style={{ background: "rgba(15,20,18,0.96)", border: "1px solid rgba(255,255,255,0.12)", backdropFilter: "blur(10px)" }} data-testid="replay-upload-pill">
          <div className="flex items-center gap-3">
            <Loader2 size={20} className="animate-spin shrink-0" style={{ color: "#D97757" }} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white truncate">{replayUpload.phase === "process" ? t("studio.replay_finalizing", "Finalisation de ta rediffusion…") : t("studio.replay_uploading", "Envoi de ta rediffusion…")} {replayUpload.progress}%</p>
              <p className="text-[11px] text-white/60 leading-snug mt-0.5" data-testid="replay-upload-hint">
                {isNativePlatform()
                  ? t("studio.replay_bg_native", "Tu peux fermer l'app 👍 l'envoi continue en arrière-plan (une notification suit la progression). Ta rediffusion s'affichera dès qu'elle sera prête. 🙏")
                  : t("studio.replay_bg_web", "Garde cette page ouverte jusqu'à la fin de l'envoi de ta rediffusion. 🙏")}
              </p>
              <div className="mt-1.5 h-1 w-full rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.15)" }}>
                <div className="h-full rounded-full transition-[width]" style={{ width: `${replayUpload.progress}%`, background: "#D97757" }} />
              </div>
            </div>
            <button onClick={() => closeOnce()} className="shrink-0 h-9 px-3 rounded-full text-xs font-semibold" style={{ background: "rgba(255,255,255,0.1)", color: "#fff" }} data-testid="replay-close-bg-btn">{t("studio.close", "Fermer")}</button>
          </div>
        </div>
      )}
      <LiveOfferingRecap liveId={live?.id} role={user?.role} open={recapOpen} onClose={() => setRecapOpen(false)} />
      {isMobile ? (isNativePlatform() ? renderMobile() : renderMobileInstall()) : phase === "setup" ? (
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] h-full w-full">
          {/* Setup rail */}
          <div className="flex flex-col h-full p-5 gap-4 overflow-y-auto z-10" style={{ background: "#131A18", borderRight: "1px solid #283832" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Radio size={20} style={{ color: "#D97757" }} />
                <h1 className="text-xl font-bold">{t("studio.create_live", "Créer un direct")}</h1>
              </div>
              <button onClick={closeStudio} className="h-9 w-9 rounded-lg grid place-items-center text-gray-400 hover:text-white" style={{ background: "#1E2925" }} data-testid="studio-close-btn"><X size={18} /></button>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#A0B0AA" }}>{t("studio.video_source", "Source vidéo")}</div>
              {SOURCES.map((s) => (
                <SourceTile key={s.key} src={s} active={source === s.key} onClick={() => setSource(s.key)} />
              ))}
            </div>

            {source === "webcam" && (
              <div className="space-y-2.5" data-testid="webcam-controls">
                <div>
                  <label className="text-xs font-medium" style={{ color: "#A0B0AA" }}>{t("studio.camera", "Caméra")}</label>
                  <select className={darkSelect} style={selectStyle} value={camId} onChange={(e) => changeCam(e.target.value)} data-testid="device-select-cam">
                    {cams.length === 0 && <option value="">{t("studio.camera_default", "Caméra par défaut")}</option>}
                    {cams.map((c, i) => <option key={c.deviceId} value={c.deviceId}>{c.label || t("studio.camera_n", "Caméra {n}").replace("{n}", i + 1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium" style={{ color: "#A0B0AA" }}>{t("studio.microphone", "Microphone")}</label>
                  <select className={darkSelect} style={selectStyle} value={micId} onChange={(e) => changeMic(e.target.value)} data-testid="device-select-mic">
                    {mics.length === 0 && <option value="">{t("studio.mic_default", "Micro par défaut")}</option>}
                    {mics.map((m, i) => <option key={m.deviceId} value={m.deviceId}>{m.label || t("studio.mic_n", "Micro {n}").replace("{n}", i + 1)}</option>)}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button onClick={toggleCam} className="flex-1 h-9 rounded-lg flex items-center justify-center gap-1.5 text-sm" style={{ background: camOn ? "#1E2925" : "#D97757", color: "#fff" }} data-testid="toggle-cam">
                    {camOn ? <Video size={15} /> : <VideoOff size={15} />} {camOn ? t("studio.camera", "Caméra") : t("studio.camera_off", "Caméra off")}
                  </button>
                  <button onClick={toggleMic} className="flex-1 h-9 rounded-lg flex items-center justify-center gap-1.5 text-sm" style={{ background: micOn ? "#1E2925" : "#D97757", color: "#fff" }} data-testid="toggle-mic">
                    {micOn ? <Mic size={15} /> : <MicOff size={15} />} {micOn ? t("studio.mic", "Micro") : t("studio.mic_off", "Micro off")}
                  </button>
                </div>
              </div>
            )}

            {source === "obs" && (
              <div className="rounded-xl p-4 flex flex-col gap-3" style={{ background: "#1A221F", border: "1px solid #283832" }} data-testid="obs-panel">
                {!form.title.trim() ? (
                  <p className="text-sm" style={{ color: "#E0A63C" }}>{t("studio.obs_add_title", "Ajoute un titre ci-dessous pour générer ta clé de stream.")}</p>
                ) : !live ? (
                  keyErr ? (
                    <div className="flex flex-col gap-2" data-testid="obs-key-error">
                      <p className="text-sm" style={{ color: "#E88A8A" }}>{keyErr}</p>
                      <Button onClick={() => genKey()} variant="outline" size="sm" className="self-start" data-testid="obs-key-retry">
                        {t("studio.retry", "Réessayer")}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm" style={{ color: "#A0B0AA" }}><Loader2 size={15} className="animate-spin" /> {t("studio.key_generating", "Génération de la clé…")}</div>
                  )
                ) : (
                  <>
                    <p className="text-xs" style={{ color: "#A0B0AA" }}>{t("studio.obs_howto", "Ouvre {obs} → Réglages → Diffusion, colle ces valeurs, puis clique « Démarrer le streaming ».").split("{obs}").map((part, i, arr) => <React.Fragment key={i}>{part}{i < arr.length - 1 && <b>OBS</b>}</React.Fragment>)}</p>
                    <CopyField label={t("studio.server_url", "Serveur (URL RTMPS)")} value={pb.rtmps_url || live.rtmps_url} testId="btn-copy-rtmps" />
                    <CopyField label={t("studio.stream_key", "Clé de stream")} value={pb.rtmps_key || live.rtmps_key} testId="btn-copy-streamkey" />
                    <p className="text-[11px]" style={{ color: "#6b7d76" }}>{t("studio.never_share_key", "Ne partage jamais ta clé de stream.")}</p>
                    <div className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-md" style={pb.cf_live ? { color: "#7fdcb0", background: "rgba(45,90,76,0.15)" } : { color: "#E0A63C", background: "rgba(224,166,60,0.12)" }} aria-live="polite" data-testid="obs-connection-status">
                      {pb.cf_live ? <><CheckCircle2 size={15} /> {t("studio.obs_received", "Flux reçu ✓ — vérifie ton aperçu, puis passe en direct")}</> : <><Loader2 size={15} className="animate-spin" /> {t("studio.obs_waiting", "En attente du flux OBS…")}</>}
                    </div>
                    {pb.cf_live && <ObsIngestHealth ingest={ingest} previewBuffering={previewBuffering} t={t} />}
                  </>
                )}
              </div>
            )}

            {source === "screen" && (
              <div className="rounded-xl p-4 text-sm" style={{ background: "#1A221F", border: "1px solid #283832", color: "#A0B0AA" }} data-testid="screen-panel">
                {hasStream ? t("studio.screen_ready", "Écran prêt à être diffusé. Clique sur « Lancer le direct ».") : t("studio.screen_pick_hint", "Sélectionne la fenêtre ou l'écran à partager.")}
                {!hasStream && (
                  <Button onClick={initScreen} className="mt-3 w-full rounded-lg" style={{ background: "#2D5A4C" }} data-testid="pick-screen-btn"><MonitorUp size={15} className="mr-1.5" /> {t("studio.pick_screen", "Choisir l'écran")}</Button>
                )}
              </div>
            )}

            <div className="space-y-2.5">
              <div>
                <label className="text-xs font-medium" style={{ color: "#A0B0AA" }}>{t("studio.title_required", "Titre *")}</label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("studio.title_ph", "Ex. Culte du dimanche 🙏")} className="text-white" style={selectStyle} data-testid="live-title-input" />
              </div>
              <div>
                <label className="text-xs font-medium" style={{ color: "#A0B0AA" }}>{t("studio.description", "Description")}</label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="text-white" style={selectStyle} data-testid="live-description-input" />
              </div>
              {myChurches.length > 0 && (
                <div>
                  <label className="text-xs font-medium" style={{ color: "#A0B0AA" }}>{t("studio.broadcast_as", "Diffuser en tant que")}</label>
                  <select value={churchId} onChange={(e) => setChurchId(e.target.value)} className="w-full h-10 rounded-lg px-3 text-sm text-white" style={selectStyle} data-testid="live-church-select">
                    <option value="">{t("studio.me_personal", "Moi (compte personnel)")}</option>
                    {myChurches.map((c) => <option key={c.id} value={c.id}>🏛️ {c.name}</option>)}
                  </select>
                  {churchId && <p className="text-[11px] mt-1" style={{ color: "#7BA697" }}>{t("studio.church_notify_hint", "Le direct apparaîtra sur la page de l'église et ses abonnés seront notifiés.")}</p>}
                </div>
              )}
              {canOffer && (
                <button onClick={() => setForm({ ...form, accept_offerings: !form.accept_offerings })} className="w-full flex items-center justify-between rounded-lg px-3 py-2.5" style={{ background: "#1E2925" }} data-testid="toggle-offerings">
                  <span className="flex items-center gap-2 text-sm"><Heart size={15} style={{ color: "#D97757" }} /> {offer.accept}</span>
                  <span className="relative inline-block h-5 w-9 rounded-full transition-colors" style={{ background: form.accept_offerings ? "#2D5A4C" : "#3a4a44" }}>
                    <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all" style={{ left: form.accept_offerings ? "18px" : "2px" }} />
                  </span>
                </button>
              )}
              {canOffer && form.accept_offerings && showGoal && (
                <div className="flex items-center gap-2 rounded-lg px-3 py-2.5" style={{ background: "#1E2925" }} data-testid="desktop-offering-goal-wrap">
                  <Target size={15} style={{ color: "#D97757" }} />
                  <span className="text-sm text-white/80 shrink-0">{offer.goal}</span>
                  <input type="number" min="0" inputMode="numeric" value={form.offering_goal} onChange={(e) => setForm({ ...form, offering_goal: e.target.value })} placeholder={t("studio.optional", "Facultatif")} className="flex-1 min-w-0 bg-transparent text-sm text-white text-right outline-none placeholder:text-white/40" data-testid="desktop-offering-goal-input" />
                  <span className="text-sm text-white/60">€</span>
                </div>
              )}
            </div>

            <div className="rounded-xl p-3 space-y-2" style={{ background: "#1A221F", border: "1px solid #283832" }} data-testid="live-thumbnail-section">
              <div className="text-xs font-semibold flex items-center gap-1.5" style={{ color: "#A0B0AA" }}>
                <ImageIcon size={14} style={{ color: "#E0A63C" }} /> {t("studio.thumbnail", "Miniature du direct")}
              </div>
              {thumbUrl ? (
                <div className="relative rounded-lg overflow-hidden" style={{ aspectRatio: "16/9", background: "#000" }}>
                  <img src={resolveMediaUrl(thumbUrl)} alt="Miniature" className="w-full h-full object-cover" data-testid="live-thumbnail-preview" />
                  <button onClick={() => setThumbUrl("")} className="absolute top-1.5 right-1.5 h-7 w-7 grid place-items-center rounded-full" style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }} data-testid="live-thumbnail-remove"><X size={14} /></button>
                </div>
              ) : (
                <FileUpload
                  accept="image/*"
                  variant="hero"
                  context="feed"
                  buttonLabel="Ajouter une miniature"
                  testId="live-thumbnail-upload"
                  onUploaded={(url) => setThumbUrl(resolveMediaUrl(url))}
                />
              )}
              <p className="text-[11px]" style={{ color: "#6b7d76" }}>{t("studio.thumb_hint", "Sert d'affiche pour le replay dans le fil. Format paysage 16:9 recommandé.")}</p>
            </div>

            {camError && <div className="text-xs px-3 py-2 rounded-lg" style={{ background: "rgba(217,119,87,0.14)", color: "#f0a888" }} data-testid="cam-error">{camError}</div>}

            <div className="rounded-xl p-3 space-y-2" style={{ background: "#1A221F", border: "1px solid #283832" }} data-testid="live-schedule-section">
              <div className="text-xs font-semibold flex items-center gap-1.5" style={{ color: "#A0B0AA" }}>
                <CalendarClock size={14} style={{ color: "#E0A63C" }} /> {scheduledId ? t("studio.schedule_edit", "Modifier la date / l'heure") : t("studio.schedule_later", "Programmer pour plus tard")}
              </div>
              <input
                type="datetime-local"
                value={scheduleAt}
                min={minDatetimeLocal()}
                max={maxDatetimeLocal()}
                onChange={(e) => setScheduleAt(e.target.value)}
                className="w-full h-9 rounded-lg px-3 text-sm text-white outline-none"
                style={{ background: "#0A0D0C", border: "1px solid #283832", colorScheme: "dark" }}
                data-testid="live-schedule-input"
              />
              <div className="space-y-1.5" data-testid="live-reminder-lead">
                <div className="text-[11px] font-semibold flex items-center gap-1.5" style={{ color: "#A0B0AA" }}>
                  <Bell size={12} style={{ color: "#E0A63C" }} /> {t("studio.remind_before", "Me rappeler avant le direct")}
                </div>
                <div className="flex items-center gap-1.5">
                  {[5, 10, 30].map((m) => {
                    const active = reminderLead === m;
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setReminderLead(m)}
                        className="flex-1 rounded-lg py-1.5 text-xs font-semibold transition-colors"
                        style={active
                          ? { background: "#E0A63C", color: "#1A1206", border: "1px solid #E0A63C" }
                          : { background: "#0A0D0C", color: "#A0B0AA", border: "1px solid #283832" }}
                        data-testid={`reminder-lead-${m}`}
                      >{m} min</button>
                    );
                  })}
                </div>
              </div>
              <Button
                onClick={scheduleBroadcast}
                disabled={scheduling || !scheduleAt || !form.title.trim()}
                className="w-full rounded-lg font-semibold"
                style={{ background: scheduleAt && form.title.trim() && !scheduling ? "#E0A63C" : "#1E2925", color: scheduleAt && form.title.trim() && !scheduling ? "#1A1206" : "#6b7d76" }}
                data-testid="live-schedule-btn"
              >
                {scheduling ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <CalendarClock size={15} className="mr-1.5" />} {scheduledId ? t("studio.schedule_update_btn", "Mettre à jour la programmation") : t("studio.schedule_btn", "Programmer le direct")}
              </Button>
              <p className="text-[11px]" style={{ color: "#6b7d76" }}>{scheduledId ? t("studio.schedule_update_hint", "La nouvelle date remplacera l'ancienne — aucun doublon ne sera créé.") : t("studio.schedule_hint", "Tes abonnés recevront une annonce, puis un rappel juste avant le direct.")}</p>
            </div>

            {gateBanner}
            {offeringsHint}
            {isBrowserSource && (
              <p className="text-xs flex items-center gap-2 px-1" style={{ color: "#6b7d76" }} data-testid="desktop-replay-note">
                <Video size={14} style={{ color: "#E0A63C" }} /> {t("studio.replay_auto_note", "La rediffusion est enregistrée automatiquement.")}
              </p>
            )}
            <Button
              onClick={goLive}
              disabled={!canGoLive || starting || !gateOk}
              className="w-full py-6 mt-auto font-bold text-base rounded-xl"
              style={{ background: canGoLive && !starting && gateOk ? "#2D5A4C" : "#1E2925", color: canGoLive && !starting && gateOk ? "#fff" : "#6b7d76" }}
              data-testid="btn-lancer-direct"
            >
              {starting ? <><Loader2 size={18} className="mr-2 animate-spin" /> {t("studio.starting", "Lancement…")}</> : <><Radio size={18} className="mr-2" /> {isBrowserSource ? t("studio.launch_live", "Lancer le direct") : t("studio.go_live", "Passer en direct")}</>}
            </Button>
            {source === "obs" && <p className="text-[11px] text-center" style={{ color: "#6b7d76" }}>{pb.cf_live ? t("studio.obs_check_preview", "Vérifie ton aperçu, puis clique pour notifier tes abonnés.") : t("studio.obs_start_to_preview", "Démarre le streaming dans OBS pour voir ton aperçu ici.")}</p>}
          </div>

          {/* Center preview */}
          <div className="relative hidden lg:flex items-center justify-center p-8" style={{ background: "#0A0D0C" }}>
            {isBrowserSource && streamRef.current ? (
              <video ref={videoRef} autoPlay playsInline muted className="max-h-full max-w-full rounded-2xl" data-testid="live-preview-video" style={{ background: "#000", aspectRatio: "16/9", objectFit: "contain", width: "100%" }} />
            ) : source === "obs" && pb.cf_live && pb.hls_url ? (
              <div className="relative w-full rounded-2xl overflow-hidden" style={{ aspectRatio: "16/9", background: "#000" }} data-testid="obs-preview">
                <HlsPlayer src={pb.hls_url} muted controls={false} testId="obs-preview-video" videoRef={obsHlsRef} compact />
                <span className="absolute top-3 left-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold" style={{ background: "rgba(224,166,60,0.95)", color: "#1A1206" }}>{t("studio.preview_not_live", "APERÇU · non diffusé")}</span>
                <ObsUnstableBox ingest={ingest} t={t} live testId="obs-network-warning-preview" />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 text-center" style={{ color: "#A0B0AA" }}>
                <div className="h-20 w-20 rounded-full grid place-items-center" style={{ background: "#1E2925" }}>
                  {source === "obs" ? <Wifi size={34} /> : <VideoOff size={34} />}
                </div>
                <p className="max-w-xs text-sm">
                  {source === "obs" ? t("studio.obs_connect_hint", "Connecte OBS puis démarre le streaming — ton aperçu apparaîtra ici avant de passer en direct.") : source === "screen" ? t("studio.screen_choose_hint", "Choisis l'écran ou la fenêtre à partager.") : t("studio.cam_allow_hint", "Autorise l'accès à ta caméra pour voir l'aperçu.")}
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* LIVE phase */
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] h-full w-full">
          <div className="relative bg-black">
            {isBrowserSource ? (
              <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-contain" data-testid="live-preview-video" />
            ) : pb.hls_url ? (
              <HlsPlayer src={pb.hls_url} muted testId="live-video" videoRef={obsHlsRef} />
            ) : (
              <div className="absolute inset-0 grid place-items-center" style={{ color: "#A0B0AA" }}>
                <div className="text-center"><Loader2 size={26} className="mx-auto animate-spin" /><p className="mt-2 text-sm">{t("studio.stream_connecting", "Connexion du flux…")}</p></div>
              </div>
            )}
            {source === "obs" && <ObsUnstableBox ingest={ingest} t={t} live testId="obs-network-warning-live" />}
            <span className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-bold uppercase tracking-wider z-20" style={{ background: "#D97757", color: "#fff" }} data-testid="live-badge-live">
              <span className="h-2 w-2 rounded-full bg-white animate-pulse" /> {t("studio.live_badge", "En direct")}
            </span>
            <span className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm z-20" style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }} data-testid="live-viewer-count">
              <Users size={14} /> {viewerCount}
            </span>
            {hasLimit && (
              <span
                className="absolute top-[52px] left-4 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold z-20"
                style={{ background: (timeUp || warn5) ? "#EF4444" : "rgba(0,0,0,0.6)", color: "#fff" }}
                data-testid="studio-live-countdown"
                title={t("studio.countdown_title", "Temps restant avant l'arrêt automatique")}
              >
                <Clock size={13} /> {timeUp ? t("studio.time_up", "Temps écoulé") : t("studio.time_left", "Temps restant {t}").replace("{t}", fmtMMSS(remainingSec))}
              </span>
            )}
            {warn5 && !timeUp && (
              <div
                className="absolute top-[52px] left-1/2 -translate-x-1/2 z-20 max-w-[90%] text-center px-4 py-2 rounded-xl text-[12px] font-semibold"
                style={{ background: "rgba(239,68,68,0.95)", color: "#fff" }}
                data-testid="studio-autostop-warning"
              >
                ⚠️ Ton direct s'arrêtera automatiquement dans {fmtMMSS(remainingSec)} (durée maximale atteinte). Pense à conclure.
              </div>
            )}
            <FloatingReactions floats={floats} />
            <ReactionButtons onSend={sendReaction} emojis={reactionEmojis} />
          </div>

          {/* Chat dock */}
          <div className="flex flex-col h-full" style={{ background: "#131A18", borderLeft: "1px solid #283832" }}>
            <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: "#283832" }}>
              <div className="min-w-0">
                <div className="font-bold truncate text-white">{form.title}</div>
                <div className="text-xs" style={{ color: "#A0B0AA" }}>{t("studio.live_comments", "Commentaires en direct")}</div>
              </div>
              <Button onClick={confirmEndLive} disabled={ending} className="rounded-lg shrink-0 font-semibold" style={{ background: "#dc2626", color: "#fff" }} data-testid="btn-terminer-direct">
                {ending ? <Loader2 size={15} className="animate-spin" /> : <><X size={15} className="mr-1.5" /> {t("studio.end_live", "Terminer le direct")}</>}
              </Button>
            </div>
            {/* Host tools during the live */}
            <div className="px-3 py-2.5 border-b space-y-2" style={{ borderColor: "#283832" }} data-testid="host-tools">
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full h-8 rounded-md px-2.5 text-sm text-white outline-none"
                style={{ background: "#0A0D0C", border: "1px solid #283832" }}
                placeholder={t("studio.live_title_ph", "Titre du direct")}
                data-testid="host-edit-title"
              />
              <div className="flex items-center gap-2">
                {canOffer && (
                  <button onClick={() => setForm({ ...form, accept_offerings: !form.accept_offerings })} className="flex-1 h-8 rounded-md text-xs flex items-center justify-center gap-1.5" style={{ background: form.accept_offerings ? "rgba(45,90,76,0.35)" : "#1E2925", color: "#fff" }} data-testid="host-toggle-offerings">
                    <Heart size={13} style={{ color: "#D97757" }} /> {offer.Plural} {form.accept_offerings ? "ON" : "OFF"}
                  </button>
                )}
                <button onClick={shareLive} className="flex-1 h-8 rounded-md text-xs flex items-center justify-center gap-1.5" style={{ background: "#1E2925", color: "#fff" }} data-testid="host-share-live">
                  <Send size={13} /> Partager
                </button>
              </div>
              {canOffer && form.accept_offerings && showGoal && offeringGoal > 0 && (
                <div className="rounded-md px-3 py-2" style={{ background: "rgba(217,119,87,0.12)", border: "1px solid rgba(217,119,87,0.3)" }} data-testid="host-offering-goal">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="flex items-center gap-1.5 font-semibold text-white/90"><Target size={13} style={{ color: "#D97757" }} /> {offer.goal}</span>
                    <span className="font-bold" style={{ color: "#D97757" }} data-testid="host-offering-goal-amounts">{Math.round(Number(pb.offering_total || 0)).toLocaleString()} {curSym} / {Math.round(offeringGoal).toLocaleString()} {curSym}</span>
                  </div>
                  <div className="h-2 w-full rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.15)" }}>
                    <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${offeringPct}%`, background: "#D97757" }} data-testid="host-offering-goal-fill" />
                  </div>
                  {Number(pb.offering_total || 0) >= offeringGoal && (
                    <div className="text-[11px] mt-1.5 font-semibold" style={{ color: "#28C291" }}>🎉 {t("live.goal_reached", "Objectif atteint — gloire à Dieu !")}</div>
                  )}
                </div>
              )}
              {canOffer && form.accept_offerings && (
                <button onClick={() => setRecapOpen(true)} className="w-full h-8 rounded-md text-xs flex items-center justify-center gap-1.5" style={{ background: "#1E2925", color: "#fff" }} data-testid="host-open-offering-recap">
                  <Gift size={13} style={{ color: "#D97757" }} /> {offer.Plural} {offer.received} · {Math.round(Number(pb.offering_total || 0)).toLocaleString()} {curSym}
                </button>
              )}
              {!isBrowserSource && (
                <div className="text-[11px] flex items-center gap-1.5" style={{ color: pb.cf_live ? "#7fdcb0" : "#E0A63C" }} data-testid="host-health">
                  {pb.cf_live ? <><CheckCircle2 size={12} /> {t("studio.obs_active", "Flux OBS actif")}</> : <><Loader2 size={12} className="animate-spin" /> {t("studio.obs_interrupted", "Flux OBS interrompu — vérifie OBS")}</>}
                </div>
              )}
              {!isBrowserSource && (
                <details className="text-[11px]" data-testid="host-obs-creds">
                  <summary className="cursor-pointer" style={{ color: "#A0B0AA" }}>{t("studio.obs_settings", "Paramètres OBS (URL & clé)")}</summary>
                  <div className="mt-2 space-y-2">
                    <CopyField label={t("studio.server_url", "Serveur (URL RTMPS)")} value={pb.rtmps_url || live?.rtmps_url} testId="host-live-rtmps-url" />
                    <CopyField label={t("studio.stream_key", "Clé de stream")} value={pb.rtmps_key || live?.rtmps_key} testId="host-live-rtmps-key" />
                  </div>
                </details>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="live-comments">
              {comments.length === 0 && <p className="text-xs" style={{ color: "#A0B0AA" }}>{t("studio.first_cheer", "Sois le premier à encourager ! 🙏")}</p>}
              {comments.filter((c) => !c.userId || !blockedIds.has(c.userId)).map((c) => (
                <div key={c.id} className="text-sm leading-snug">
                  {c.userId && c.userId !== user?.id ? (
                    <button type="button" className="font-semibold hover:underline" style={{ color: c.isHost ? "#7fdcb0" : "#fff" }}
                      onClick={() => setBlockTarget({ id: c.userId, name: c.name })} title={t("block.action", "Bloquer {name}").replace("{name}", c.name || "")} data-testid={`studio-chat-author-${c.id}`}>{c.name}</button>
                  ) : (
                    <span className="font-semibold" style={{ color: c.isHost ? "#7fdcb0" : "#fff" }}>{c.name}</span>
                  )}
                  <span className="ml-1.5" style={{ color: "#cfd8d4" }}>{c.text}</span>
                </div>
              ))}
              <div ref={commentsEndRef} />
            </div>
            <BlockUserDialog open={!!blockTarget} onOpenChange={(o) => { if (!o) setBlockTarget(null); }} user={blockTarget} testidPrefix="studio-block" />
            <div className="p-2 border-t flex items-center gap-1.5" style={{ borderColor: "#283832" }}>
              <button onClick={() => { if (wsRef.current?.readyState === 1) { wsRef.current.send(JSON.stringify({ type: "chat", text: "🙏 Amen !" })); } }} className="h-9 px-2.5 rounded-full text-sm shrink-0" style={{ background: "#1E2925" }} data-testid="live-amen-btn">🙏</button>
              <input className="flex-1 min-w-0 h-9 rounded-full px-3 text-sm outline-none text-white" style={{ background: "#0A0D0C", border: "1px solid #283832" }} value={commentText} onChange={(e) => setCommentText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendComment()} placeholder={t("studio.comment_ph", "Commenter…")} maxLength={500} data-testid="live-comment-input" />
              <button onClick={sendComment} className="h-9 w-9 rounded-full grid place-items-center shrink-0" style={{ background: "#2D5A4C", color: "#fff" }} data-testid="live-comment-send"><Send size={14} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveStudio;
